/**
 * jk-filter-results.js — выдача квартир inline-фильтра на карточке ЖК.
 *
 * План jk-smart-filter, блоки 1.3 / 1.7.2 / 1.7.4 (2026-07-28).
 * Контейнер: #jkFilterResults (разметка 1.2, app/templates/pages/jk_content.php).
 * Стили: css/jk-filter.css. Данные: window.BroniJkFilter (public/js/jk-filter.js).
 *
 * Почему отдельный файл, а не продолжение jk-filter.js:
 *   jk-filter.js — состояние фильтра и сеть; здесь — только представление.
 *   Модуль НЕ ходит в сеть сам и НЕ знает про flat_api.php: он подписывается на
 *   контракт window.BroniJkFilter и рисует то, что дали. Поэтому его можно
 *   тестировать на подставном фильтре, а фильтр — без выдачи.
 *
 * Что делает:
 *   • рисует карточки квартир (плитка), компактные строки (список),
 *     карточки ПЛАНИРОВОК ЖК (третий вид, блок 1.7.2) или СВОДКУ по комнатности
 *     (четвёртый вид) — один и тот же контейнер, разные карточки;
 *   • стартует с «Планировок» (владелец, 28.08.2026), когда фильтр по умолчанию
 *     отдаёт планировки (`defaultMode`): первые десять карточек, дальше по десять
 *     за клик «Показать ещё». НЕ перебивает выбор вернувшегося человека —
 *     localStorage пишется только по клику на переключателе;
 *   • переиспользует SSR-контент контейнера (таблица типов планировок,
 *     `[data-ssr="flats"]`): каркас выдачи строится рядом с ней, таблица уходит
 *     ровно в момент прихода первого набора данных и остаётся на месте, если
 *     фильтр не поднялся вовсе. Так секция не мигает пустотой, а бот/no-JS
 *     всегда видят планировки;
 *   • помнит выбранный вид в localStorage (jk_filter_view);
 *   • «Показать ещё» → BroniJkFilter.loadMore() (по 12 квартир, PER_PAGE фильтра);
 *     страница ДОПИСЫВАЕТСЯ (без перерисовки уже показанного) и дедуплицируется;
 *   • пустое состояние с кнопкой «Сбросить» → BroniJkFilter.reset();
 *   • ссылку «Открыть в полном каталоге →» — из payload.catalogUrl (слепок
 *     фильтра НА МОМЕНТ ЗАПРОСА, а не живых контролов);
 *   • при onFacets(null) (скрытый ЖК / нет квартир) не рисует НИЧЕГО.
 *
 * Защита от гонки — сравнение непрозрачной строки payload.sig, которую считает
 * САМ фильтр в момент запроса. Разбирать её здесь нельзя: перечисление полей
 * руками означало бы, что новое поле фильтра в 1.4+ молча выпадет из защиты.
 *
 * Клик по карточке КВАРТИРЫ открывает существующую модалку
 * window.openFlatOverlay(id) — своей модалки у выдачи нет. Порядок подключения
 * (flat-overlay.js → jk-filter.js → этот файл) пинится integration-тестом.
 *
 * Клик по карточке ПЛАНИРОВКИ объявляется событием `bronidom:plan-card-click`
 * (bubbles) с detail {rooms, areaMin, areaMax, count, label, card}, и здесь же
 * подписан аккордеон (блок 1.7.3): под карточкой разворачивается список её
 * квартир. Идентичность карточки — ИМЕННО (rooms, area_min, area_max): plan_id
 * между запросами не стабилен.
 *
 * Блок 1.7.8 — оформление раскрытия (без изменений в логике 1.7.3):
 *   • панель раскрывается и сворачивается ПЛАВНО (высота 0fr→1fr в css), а на
 *     время запроса держит скелетон с резервом высоты — контент под ней больше
 *     не прыгает дважды за одно нажатие;
 *   • внутри панели — КРУПНЫЙ чертёж планировки и её сводка (площадь, «от N ₽»,
 *     сколько квартир, срок сдачи), справа список квартир с ₽/м², сроком корпуса
 *     и бейджем «Апартаменты». Ни одного нового запроса: чертёж — тот же адрес,
 *     что на обложке карточки, остальное уже есть в ответах;
 *   • вид «Планировки» показывает сначала ДЕВЯТЬ карточек — три полных ряда по
 *     три, — дальше по девять за клик «Показать ещё» (у ЖК «Пушкино Град» их
 *     118 — простынёй это не листается; с 28.08.2026 порция считается в
 *     карточках, а не в рядах, и кратна ряду, чтобы последний не обрывался).
 *     Дорисовка идёт из уже полученных данных, без сети.
 *
 * Аккордеон — СИНГЛТОН (раскрыта максимум одна планировка). Причина не в
 * оформлении: каждое раскрытие — отдельный запрос через
 * BroniJkFilter.fetchPlanFlats() в ТО ЖЕ ведро лимита, что и выдача (120/60).
 * Повторное раскрытие той же карточки при том же фильтре берётся из кэша
 * фильтра и сети не касается; повторный клик по раскрытой — сворачивает.
 *
 * Правила проекта: IIFE + guard, никаких inline-обработчиков (CSP), тексты из
 * API — только textContent (innerHTML в файле нет вовсе).
 */
(() => {
    'use strict';

    const api = window.BroniJkFilter;
    // Фильтр не поднялся (не карточка ЖК / нет разметки) — рисовать нечего и негде.
    if (!api || !api.resultsEl) return;

    const root = api.resultsEl;
    // ⛔ ВКЛАДКА «ПЛАНИРОВКИ» ВИТРИНЫ КВАРТИР ДОМА (27.09.2026, `data-on-showcase` печатает
    // сервер в куске `?part=plans`, js/flats-plans-view.js). Фильтром блока там управляет
    // витрина, её же переключатель видов; от блока нужна только выдача «Планировок».
    const ON_SHOWCASE = Boolean(root.closest && root.closest('[data-on-showcase]'));

    // ═══ Константы ═══
    const VIEW_KEY = 'jk_filter_view';
    // Третий вид живёт ТОЛЬКО с фильтром, который умеет режимы выдачи. У
    // человека в браузере может лежать закэшированный старый jk-filter.js —
    // тогда кнопки «Планировки» просто не будет, вместо поломки всей выдачи.
    const MODES_SUPPORTED = typeof api.setResultMode === 'function';
    // Раскрытие квартир планировки (1.7.3) требует метода фильтра. У человека в
    // браузере может лежать закэшированный jk-filter.js без него — тогда карточка
    // просто не притворяется раскрывающейся (без aria-expanded и без панели).
    const EXPAND_SUPPORTED = typeof api.fetchPlanFlats === 'function';
    // ⛔ «Сводка» ПЕРВОЙ и только вместе с режимами: она рисуется из тех же
    // floorplans, что и «Планировки» (modeOf ниже), поэтому со старым
    // закэшированным jk-filter.js её просто нет — как и третьего вида.
    const VIEWS = MODES_SUPPORTED ? ['summary', 'grid', 'list', 'plans'] : ['grid', 'list'];
    /**
     * Стартовый вид (блок 1.7.4). Берётся ИЗ КОНТРАКТА фильтра (`defaultMode`),
     * своей копии дефолта здесь быть не должно: разъехавшись, они дали бы первый
     * запрос за одним набором данных и первый экран под другой.
     * Старый закэшированный jk-filter.js поля не отдаёт — тогда, как и раньше,
     * стартуем с плитки (третьего вида у него всё равно нет).
     */
    const DEFAULT_MODE = MODES_SUPPORTED && typeof api.defaultMode === 'string'
        ? api.defaultMode
        : 'flats';
    /**
     * ⛔ СТАРТОВЫЙ ВИД — «Планировки» (владелец, 28.08.2026: «по умолчанию
     * открывался раздел планировки, максимум 10 планировок, потом плашка
     * „показать ещё“»).
     *
     * История: с 18.08 стартовой была «Сводка» — ради «при открытии ни одного
     * чертежа». Владелец это решение отменил; цену картинок держит порция
     * PLANS_FIRST: на открытии создаётся не больше девяти <img>, а не 118.
     *
     * Чужой выбор это НЕ перебивает: localStorage пишется только внутри
     * setView(), то есть по клику. Значит смена умолчания видна исключительно
     * тем, кто вид ни разу не выбирал сам; кто однажды нажал «Сводку» — увидит
     * сводку, пока не нажмёт другое.
     *
     * Запросов не добавляет ни одного: modeOf('plans') === DEFAULT_MODE.
     * Ссылка `?fv=flats` по-прежнему открывает квартиры (urlMode ниже).
     */
    const DEFAULT_VIEW = DEFAULT_MODE === 'plans' && VIEWS.indexOf('plans') >= 0
        ? 'plans'
        : 'grid';
    const VIEW_LABELS = {
        summary: 'Сводка', grid: 'Плитка', list: 'Список', plans: 'Планировки',
    };
    // Короткая подпись для узкого экрана: полные подписи в строку на 390px не
    // помещаются и выдавливают горизонтальный скролл всей страницы. Какая из двух
    // видна — решает CSS; в разметке всегда обе, поэтому вид не «прыгает» при
    // повороте экрана и не требует пересборки кнопок.
    // ⛔ С четвёртой кнопкой тесно уже и «Сводке»: 4 кнопки делят те же ~324px.
    // ⛔ У «Планировок» КОРОТКОЙ ПОДПИСИ НЕТ (слово владельца 28.09.2026: «на телефоне
    // планы, а на компе планировки называются — чтоб не путать»). Вид называется одинаково
    // на любой ширине; место под полное слово на 390 px дают правила css/jk.css
    // («Планировки» на телефоне): значки кнопок там убраны, кнопка планировок шире.
    const VIEW_LABELS_SHORT = { summary: 'Свод' };
    const VIEW_ICONS = {
        summary: 'fas fa-layer-group',
        grid: 'fas fa-table-cells-large',
        list: 'fas fa-list',
        plans: 'fas fa-drafting-compass',
    };
    /**
     * Вид выдачи → режим данных фильтра: плитка и список — те же квартиры.
     * ⛔ «Сводка» просит ПЛАНИРОВКИ, а не квартиры: она группирует floorplans по
     * комнатности. Свой запрос ей не нужен вовсе — рисует то, что фильтр уже
     * загрузил для вида «Планировки», поэтому и счётчики у них общие.
     */
    const modeOf = v => (v === 'plans' || v === 'summary' ? 'plans' : 'flats');
    // Минимальная скидка (%), при которой показываем уценку. SSOT — PHP
    // FlatHandler::DISCOUNT_MIN_PCT, тот же порог у карточки каталога
    // (public/js/flats.js). Микроскидки <3% — шум округления фидов.
    const DISCOUNT_MIN_PCT = 3;
    // Ширина обложки планировки для WebP-прокси: бокс 200px × ретина.
    const PLAN_COVER_WIDTH = 400;
    // Чертёж в маленькой карточке сводки: бокс ~240px на экране с ×2.
    const PLAN_TILE_WIDTH = 480;
    // Крупный чертёж ВНУТРИ раскрытой панели (1.7.8). Бокс на экране — 256px
    // (десктоп) / ~350px (телефон), 560 закрывает оба с запасом под ретину и
    // остаётся ОДНИМ ключом кэша прокси на обе раскладки. Тот же исходный адрес,
    // что у обложки карточки, — новых запросов к данным раскрытие не делает.
    const PLAN_LARGE_WIDTH = 560;
    // I.7: потолок «это заглушка, а не чертёж». img-thumb.php на провал источника
    // отдаёт 200 + прозрачный пиксель 1×1 — по натуральной ширине он отличим от
    // любой настоящей планировки (сотни пикселей). 2, а не 10: запас нужен от
    // округлений, а не от мелких планов, которых не бывает.
    const THUMB_STUB_MAX_PX = 2;
    // Вид «Планировки» не пагинируется сервером: у ЖК «Пушкино Град» ответ несёт
    // 118 карточек одним куском (он лёгкий — ~26 КБ). Простынёй это листать
    // невозможно, поэтому режем ПОКАЗ на клиенте: сначала десять карточек,
    // дальше — по десять за клик «Показать ещё». Сети это не касается вовсе.
    //
    // ⛔ ДЕВЯТЬ — ПОРЦИЯ, КРАТНАЯ РЯДУ. Утром 28.08.2026 владелец просил «максимум
    // 10 планировок, потом плашка „показать ещё“», и порция была 10. Вечером того
    // же дня принят вид с ТРЕМЯ колонками (css/jk-filter.css), и десятая карточка
    // стала висеть одна в четвёртом ряду — обрыв, который читается как поломка
    // вёрстки, а не как «дальше есть ещё». Девять — это ровно три полных ряда,
    // ближайшее к просьбе число, которое ряд не рвёт.
    // ⛔ Порция считается В КАРТОЧКАХ, а не в рядах: раньше первый экран был «два
    // ряда» и на телефоне (две колонки) давал 4 карточки, а на 1920px — 8..10, то
    // есть число зависело от ширины окна и на разных экранах владелец видел разное.
    //
    // ⛔ Серверной пагинации у этого вида НЕТ и она не нужна: «Сводка» и опросник
    // строятся из ТОГО ЖЕ полного набора floorplans (modeOf('summary') ===
    // 'plans', quizRemember(items)) — страницы по 10 с сервера оставили бы их без
    // данных, а каждый клик тратил бы ведро 120/60 и плодил ключи в общем Redis.
    const PLANS_FIRST = 9;
    const PLANS_STEP = 9;
    /**
     * Фактическая высота строки квартиры, px. ЗАМЕРЕНО на стенде (2026-07-28,
     * `getBoundingClientRect().height`), а не выведено из min-height: у строки
     * теперь ДВЕ строки текста (цена + ₽/м²), плюс бейджи корпуса.
     *   • 1920: 62.2–62.8 → 62;
     *   • ≤767: 87.8–143 (перенос бейджей и цены), среднее ≈109 → 108.
     * По этим числам считается РЕЗЕРВ высоты под скелетон, чтобы контент под
     * панелью не прыгал дважды за одно нажатие.
     */
    const PANEL_ROW_H = 62;
    const PANEL_ROW_H_NARROW = 108;
    // Потолок списка = max-height .jk-filter__panel-rows в css (десктоп/≤767).
    // Резерв выше него бессмыслен: список всё равно прокручивается внутри себя.
    const PANEL_LIST_MAX_H = 420;
    const PANEL_LIST_MAX_H_NARROW = 320;
    // Граница мобильной раскладки — та же, что в @media css.
    const PANEL_NARROW_MAX_W = 767;
    // Сколько полос показывать, когда карточка не сказала, сколько у неё квартир.
    const PANEL_SKELETON_DEFAULT = 3;
    // Длительности анимации панели. Дублируют transition в css: JS обязан снять
    // узел ПОСЛЕ того, как сворачивание доиграло, иначе панель исчезает рывком.
    // ⛔ Парность гейтится кросс-файловым пином (Integration): css-длительность
    // `.jk-filter__panel--closing` обязана быть ≤ PANEL_CLOSE_MS.
    const PANEL_CLOSE_MS = 180;
    // Раскрыта одна планировка за раз, поэтому id панели — константа (нужен для
    // aria-controls: скринридер связывает кнопку с тем, что она открывает).
    const PANEL_ID = 'jkFilterPlanPanel';
    // Цель метрики читается делегатом [data-ym] на document ПОСЛЕ нашего
    // обработчика — значит она успевает описать именно то действие, которое
    // клик выполнил: раскрытие или сворачивание. Историческая
    // `jk_filter_plan_card` остаётся ТОЛЬКО фолбэку без раскрытия — иначе
    // сворачивания засоряли бы её и цифры кликов по планировкам врали бы.
    const YM_CARD = 'jk_filter_plan_card';
    const YM_EXPAND = 'jk_filter_plan_expand';
    const YM_COLLAPSE = 'jk_filter_plan_collapse';
    const YM_SUM_EXPAND = 'jk_filter_summary_expand';
    const YM_SUM_COLLAPSE = 'jk_filter_summary_collapse';

    // ── Сводка (четвёртый вид) ──
    // Минимальная видимая доля полоски, %. Комнатность с одной квартирой против
    // сотни даёт 1% — полоска в 2px читается как «ноль», и строка выглядит
    // пустой. Это оформление, а не число: настоящее количество стоит в счётчике.
    const SUMMARY_BAR_MIN_PCT = 6;
    // id тела строки — для aria-controls. Строк несколько, поэтому к префиксу
    // добавляется индекс.
    const SUMMARY_BODY_ID = 'jkFilterSummaryBody';
    /**
     * Плиток в ПЕРВОЙ порции раскрытой строки и шаг «показать ещё».
     *
     * ⛔ БЕЗ ПОРЦИЙ РАСКРЫТАЯ СТРОКА РЕЖЕТ ПЛИТКИ. Замер 18.08 по 30 крупнейшим
     * ЖК: из 140 групп комнатности 91 (65 %) не помещалась в тело строки на
     * телефоне — VEER 1-комн даёт 45 плиток, City Bay 3-комн 43, а видно было
     * по 10. Второй эффект того же: раскрытие разом создавало 45 <img>.
     * Вид «Планировки» ту же нагрузку режет ровно так же (growPlans), поэтому и
     * здесь средство одно — порция + кнопка внутри тела, а не вложенная
     * прокрутка (на телефоне скролл внутри скролла не поймать пальцем).
     */
    const SUMMARY_TILES_FIRST = 8;
    const SUMMARY_TILES_STEP = 12;
    // Задержка появления соседней строки, мс. Взгляд успевает пробежать сверху
    // вниз, а не получает шесть строк одним хлопком. Число из принятого
    // черновика (hero-draft-backup/FINAL-8901_2026-08-18_0008/flats-summary.js).
    const SUMMARY_ROW_DELAY_MS = 45;

    // ═══ Форматирование ═══
    const nf = new Intl.NumberFormat('ru-RU');
    const fmtPrice = v => nf.format(Math.round(parseFloat(v) || 0)) + ' ₽';
    // Точка, а не запятая — как в каталоге (flats.js) и в модалке квартиры.
    // Одна и та же квартира не должна показывать «42,1» здесь и «42.1» там.
    const fmtArea = v => {
        const n = parseFloat(v) || 0;
        return n % 1 === 0 ? n.toFixed(0) : n.toFixed(1);
    };
    const roomsLabel = v => {
        const n = parseInt(v, 10);
        if (!Number.isInteger(n) || n < 0) return 'Квартира';
        return n === 0 ? 'Студия' : n + '-комн.';
    };

    /**
     * Адрес картинки из API. Пропускаем только НАШ относительный путь и http(s)
     * без кредов — паритет с PHP LayoutPolicy. Иначе битая запись фида даёт
     * исполняемый src (`javascript:`) или чужой хост:
     *   • `/\evil.com/x.png` — браузер по WHATWG нормализует `\` в `/`, то есть
     *     это протокол-относительный адрес чужого сайта, а не наш путь;
     *   • `http://user:pass@host/` — либо фишинг-обманка в адресе, либо утечка.
     */
    const safeSrc = raw => {
        const s = String(raw == null ? '' : raw).trim();
        if (/^\/[^/\\]/.test(s)) return s;
        if (!/^https?:\/\//i.test(s)) return '';
        const authority = s.replace(/^https?:\/\//i, '').split(/[/?#]/)[0];
        return authority.indexOf('@') === -1 ? s : '';
    };

    /**
     * Внешняя картинка → наш WebP-прокси (кэш 7 дней, SSRF-guard внутри
     * img-thumb.php). Паритет с `thumbUrl()` в public/js/jk.js и PHP-хелпером
     * `thumbProxyUrl()` в SSR-слайдах.
     *
     * Зачем именно здесь: обложки планировок — оригиналы из S3 застройщика по
     * ~160 КБ штука, и грузятся они ВСЕ сразу (у City Bay кластеров под две
     * сотни — это десятки мегабайт на открытие вида). Свои и относительные
     * адреса не проксируем: они и так наши.
     */
    const thumbUrl = (url, w) => {
        if (!/^https?:\/\//i.test(url)) return url;
        if (/^https?:\/\/(www\.)?bronidom\.ru\//i.test(url)) return url;
        return '/img-thumb.php?src=' + encodeURIComponent(url) + '&w=' + (w || 1200);
    };

    /**
     * СТЕНД: общая калитка картинок (js/utils.js), одна на весь сайт.
     *
     * ⛔ ЗАЧЕМ ОНА ЗДЕСЬ, ЕСЛИ ВЫШЕ УЖЕ ЕСТЬ thumbUrl(). Соседний уменьшитель
     * НАМЕРЕННО пропускает боевой домен как есть (строка 293: адреса
     * `bronidom.ru` он не проксирует, на проде это свой же файл на своём диске).
     * На локальной копии сайта это означало бы поход на прод. Общая калитка
     * уводит любой абсолютный адрес и ТОЛЬКО на стенде: на проде первая же её
     * строка возвращает адрес как есть, то есть разметка тут дословно прежняя.
     *
     * ⛔ ЧТО ЛЕЧИМ (доводка 5, находка 29, замер верификатора 5). Карточка ЖК →
     * «Планировки» → раскрыть карточку → нажать на чертёж: лупа отдавала общему
     * лайтбоксу СЫРОЙ адрес, и стенд шёл за чертежом на CDN застройщика
     * (`bitrix.1dogma.net` на dogma-park, `cdn.brusnika.ru` на kvartal-metronom).
     * Плюс сама ссылка-лупа (`<a target="_blank">`) вела туда же без всякого JS.
     */
    const standSrc = (u, w) => (window.standSrc ? window.standSrc(u, w) : u);

    const el = (tag, cls, text) => {
        const node = document.createElement(tag);
        if (cls) node.className = cls;
        if (text !== undefined && text !== null && text !== '') node.textContent = String(text);
        return node;
    };

    const icon = cls => {
        const i = document.createElement('i');
        i.className = cls;
        i.setAttribute('aria-hidden', 'true');
        return i;
    };

    /**
     * seo-master I.7 (2026-08-02): БЕЛАЯ обложка обязана выглядеть как «плана нет».
     *
     * Раньше фолбэк-иконка ставилась ТОЛЬКО при пустом layout_url, и у ЖК с живым
     * адресом плитка молча оставалась пустым прямоугольником. Владелец увидел это
     * на Малиново (id 117, фид Основы): вкладка «Планировки» — белые плитки.
     *
     * Причин ДВЕ, и ловятся они по-разному:
     *
     *   1. Честный сбой — 404 у CDN застройщика, мёртвая ссылка в фиде. Стреляет
     *      событие `error` (мёртвых ссылок в фидах много — ADR-094/095).
     *
     *   2. ГЛАВНЫЙ случай (замер на проде 02.08): наш прокси img-thumb.php на
     *      провал источника отвечает НЕ ошибкой, а `200 + прозрачный пиксель 1×1`
     *      (74 байта, заголовок `X-Thumb-Fallback: 1`) — 6 плиток из 8 у Малиново.
     *      Исходник при этом ЖИВОЙ: crmwebexternal.gk-osnova.ru отдаёт 200 и 1,35 МБ
     *      за 2,3 с, просто не пролезает в таймаут прокси на холодном кэше. Никакой
     *      ошибки браузер не видит, `error` не сработает — отличаем заглушку по
     *      НАТУРАЛЬНОМУ размеру: настоящая планировка это сотни пикселей.
     *      (Известная мина проекта: тем же способом её ловит карта.)
     *
     * Оба пути ведут в один и тот же вид «нечего показать» — ту же иконку и тот же
     * `--empty`-класс, что при пустом адресе. Отдельного «сломанного» вида нет:
     * посетителю всё равно, почему чертежа нет.
     *
     * CSP: слушатели вешаются кодом, inline-атрибуты onerror/onload запрещены.
     *
     * @param {HTMLElement} box   контейнер обложки (у него уже стоит базовый класс)
     * @param {HTMLElement} img   картинка внутри box
     * @param {string} emptyCls   класс-модификатор «нечего показать»
     * @param {string} iconCls    FA-класс иконки-заглушки
     */
    const coverFallback = (box, img, emptyCls, iconCls) => {
        const drop = () => {
            // Картинка может лежать в обёртке (в панели раскрытия — в ссылке-лупе),
            // поэтому убираем ПРЯМОГО потомка обложки: иначе осталась бы пустая
            // кликабельная рамка поверх иконки. Цикл конечен — вверх до отрыва.
            let doomed = img;
            while (doomed.parentNode && doomed.parentNode !== box) doomed = doomed.parentNode;
            if (doomed.parentNode !== box) return; // обложку уже перерисовали
            box.removeChild(doomed);
            box.classList.add(emptyCls);
            box.appendChild(icon(iconCls));
        };
        img.addEventListener('error', drop, { once: true });
        img.addEventListener('load', () => {
            if ((img.naturalWidth || 0) <= THUMB_STUB_MAX_PX) drop();
        }, { once: true });
        // Сегодня обложку строят синхронно сразу после `img.src = …`, и `load`
        // всегда успевает прийти к слушателю. Но если завтра карточку соберут
        // асинхронно (или картинка окажется в кэше), событие выстрелит ДО подписки
        // и заглушка молча останется на экране. Разовая проверка уже готовой
        // картинки закрывает этот случай, а `drop` идемпотентен.
        if (img.complete && (img.naturalWidth || 0) <= THUMB_STUB_MAX_PX) drop();
    };

    const flatId = f => {
        const id = parseInt(f && f.id, 10);
        return Number.isInteger(id) && id > 0 ? id : 0;
    };

    /**
     * Человек попросил систему не двигать интерфейс (вестибулярные нарушения,
     * укачивание). Спрашиваем ПЕРЕД каждым раскрытием, а не один раз на старте:
     * настройку меняют на лету, и панель обязана это услышать сразу.
     */
    function motionOff() {
        return mediaMatches('(prefers-reduced-motion: reduce)');
    }

    function mediaMatches(query) {
        try {
            return typeof window.matchMedia === 'function'
                && window.matchMedia(query).matches === true;
        } catch (e) {
            return false;
        }
    }

    /**
     * Фактические высоты строки, замеренные на прошлых раскрытиях (0 = ещё не
     * мерили). ⛔ Объявление стоит ВЫШЕ читающей panelMetrics осознанно: `const`
     * не поднимается, и при первом же переносе вызова выше по файлу чтение
     * ушло бы во временную мёртвую зону (ReferenceError вместо резерва).
     */
    const rowHMeasured = { wide: 0, narrow: 0 };

    /**
     * Метрики списка квартир для расчёта резерва высоты. Раскладка меняет и
     * высоту строки, и потолок списка — считать резерв одними десктопными
     * числами значило бы промахиваться на телефоне ровно вдвое.
     */
    function panelMetrics() {
        const narrow = mediaMatches('(max-width: ' + PANEL_NARROW_MAX_W + 'px)');
        // Замеренная на ЖИВОЙ раскладке высота строки бьёт константу: число
        // переносов у строки плавающее (бейдж корпуса, ₽/м², «Апартаменты»), и
        // одно среднее число промахивается на конкретном ЖК. Замер на проде
        // 31.07 (Дюна, 390): резерв 2×108=216px стоял против фактических 166px,
        // и через полсекунды после нажатия всё под панелью прыгало на 50px
        // ВВЕРХ — второй прыжок за одно нажатие. Константа остаётся первым
        // приближением для самого первого раскрытия, дальше работает факт.
        const measured = narrow ? rowHMeasured.narrow : rowHMeasured.wide;
        return {
            row: measured > 0 ? measured : (narrow ? PANEL_ROW_H_NARROW : PANEL_ROW_H),
            max: narrow ? PANEL_LIST_MAX_H_NARROW : PANEL_LIST_MAX_H
        };
    }

    /**
     * Запомнить фактическую высоту строки квартиры — по ней считается резерв
     * СЛЕДУЮЩЕГО раскрытия. Берём первую строку: она типична, а среднее по
     * списку смазывали бы единичные двухэтажные бейджи.
     */
    function rememberRowHeight(listBox) {
        const first = listBox && listBox.firstElementChild;
        if (!first || typeof first.getBoundingClientRect !== 'function') return;
        const h = Math.round(first.getBoundingClientRect().height);
        if (h <= 0) return;
        if (mediaMatches('(max-width: ' + PANEL_NARROW_MAX_W + 'px)')) rowHMeasured.narrow = h;
        else rowHMeasured.wide = h;
    }

    /** Процент уценки застройщика или 0, если её нет / она ниже порога. */
    const discountPct = f => {
        const was = parseInt(f.price_old, 10) || 0;
        const now = parseInt(f.price, 10) || 0;
        if (was <= 0 || now <= 0 || was <= now) return 0;
        const pct = Math.floor((was - now) / was * 100);
        return pct >= DISCOUNT_MIN_PCT ? pct : 0;
    };

    /** Этаж / корпус / юр-маркировка — то, что отличает квартиры ОДНОГО ЖК. */
    function metaParts(f) {
        const parts = [];
        const floor = parseInt(f.floor, 10);
        if (Number.isInteger(floor)) {
            const total = parseInt(f.floors_total, 10);
            parts.push(floor + (Number.isInteger(total) && total > 0 ? '/' + total : '') + ' эт.');
        }
        // Корпус приходит в выдаче ЖК (flat_api view=flats, keepSection) — именно он
        // связывает квартиру с выбранной плашкой срока сдачи.
        const section = String(f.section == null ? '' : f.section).trim();
        if (section !== '') parts.push(section);
        // Апартаменты — нежилое помещение (нет прописки, выше налог). Юридическая
        // маркировка обязана быть видна в списке, а не только внутри карточки.
        if (f.is_apartment) parts.push('Апартаменты');
        return parts;
    }

    // ═══ Карточка (плитка) ═══
    function planBox(f, cls) {
        // layout_url решён СЕРВЕРНО (LayoutPolicy: поэтажники/склейки вычищены на
        // бэке) — фронт его не пересчитывает. Нет плана → иконка (28.09.2026: обложку ЖК
        // в место планировки не подставляем — «в месте планировки только чертёж»).
        // Явно маркированный card_render_url — отдельное фото с подписью (10.10.2026).
        const planSrc = safeSrc(f.layout_url);
        const renderSrc = planSrc === '' ? safeSrc(f.card_render_url) : '';
        const src = planSrc || renderSrc;
        const box = el('span', src === '' ? cls + ' ' + cls + '--empty' : cls);
        if (renderSrc) box.classList.add('jk-filter__render-cover');
        if (src === '') {
            box.appendChild(icon('fas fa-building'));
            return box;
        }
        const img = document.createElement('img');
        // setAttribute, а не IDL-свойства: часть движков (и jsdom в тестах) не
        // отражает img.loading/img.decoding обратно в разметку.
        // ⛔ ПОРЯДОК: атрибуты и обработчики ДО src — см. planCover().
        img.setAttribute('loading', 'lazy');
        img.setAttribute('decoding', 'async');
        img.alt = renderSrc ? 'Рендер ЖК' : 'Планировка ' + roomsLabel(f.rooms) + ', ' + fmtArea(f.area) + ' м²';
        coverFallback(box, img, cls + '--empty', 'fas fa-building');
        // ⛔ ЧЕРЕЗ ПРОКСИ, а не оригиналом. Здесь стоял голый `img.src = src`, и
        // карточка квартиры тянула чертёж застройщика в натуральную величину:
        // замер на Малиново 02.08 — кадры 5000×2812 (14 Мп) в боксе 82-358 px,
        // 42 Мп сырых картинок на одно открытие вкладки. Ширина та же, что у
        // плиток планировок: бокс одного порядка.
        img.src = thumbUrl(src, PLAN_COVER_WIDTH);
        box.appendChild(img);
        if (renderSrc) box.appendChild(el('span', 'jk-filter__render-caption', 'Рендер ЖК'));
        return box;
    }

    function anchor(f, cls) {
        const a = document.createElement('a');
        a.className = cls;
        // Настоящая ссылка, а не div: Ctrl+клик открывает квартиру в новой вкладке,
        // а без JS карточка остаётся кликабельной. Обычный клик перехватываем.
        a.href = '/flat.php?id=' + flatId(f);
        a.dataset.flatId = String(flatId(f));
        return a;
    }

    function gridCard(f) {
        const card = anchor(f, 'jk-filter__card');
        const plan = planBox(f, 'jk-filter__card-plan');
        plan.appendChild(el('span', 'jk-filter__card-tag', roomsLabel(f.rooms)));
        const pct = discountPct(f);
        if (pct > 0) plan.appendChild(el('span', 'jk-filter__card-badge', '−' + pct + '%'));
        card.appendChild(plan);

        const body = el('span', 'jk-filter__card-body');
        const row = el('span', 'jk-filter__card-row');
        row.appendChild(el('span', 'jk-filter__card-area', fmtArea(f.area) + ' м²'));
        row.appendChild(el('span', 'jk-filter__card-price', fmtPrice(f.price)));
        body.appendChild(row);

        const sub = el('span', 'jk-filter__card-sub');
        const m2 = parseInt(f.price_m2, 10) || 0;
        sub.appendChild(el('span', 'jk-filter__card-m2', m2 > 0 ? nf.format(m2) + ' ₽/м²' : ''));
        if (pct > 0) sub.appendChild(el('span', 'jk-filter__card-old', fmtPrice(f.price_old)));
        body.appendChild(sub);

        const meta = metaParts(f);
        if (meta.length > 0) body.appendChild(el('span', 'jk-filter__card-meta', meta.join(' · ')));
        card.appendChild(body);
        return card;
    }

    // ═══ Строка (список) ═══
    function listRow(f) {
        const row = anchor(f, 'jk-filter__row');
        row.appendChild(planBox(f, 'jk-filter__row-plan'));

        const info = el('span', 'jk-filter__row-info');
        const specs = [roomsLabel(f.rooms), fmtArea(f.area) + ' м²'].concat(metaParts(f));
        info.appendChild(el('span', 'jk-filter__row-specs', specs.join(' · ')));
        row.appendChild(info);

        const price = el('span', 'jk-filter__row-price');
        const val = el('span', 'jk-filter__row-price-val', fmtPrice(f.price));
        const pct = discountPct(f);
        if (pct > 0) val.appendChild(el('span', 'jk-filter__row-badge', '−' + pct + '%'));
        price.appendChild(val);
        const m2 = parseInt(f.price_m2, 10) || 0;
        if (m2 > 0) price.appendChild(el('span', 'jk-filter__row-m2', nf.format(m2) + ' ₽/м²'));
        row.appendChild(price);
        return row;
    }

    // ═══ Карточка планировки (третий вид) ═══
    const pluralRu = (n, one, few, many) => {
        const abs = Math.abs(n) % 100;
        const tail = abs % 10;
        if (abs > 10 && abs < 20) return many;
        if (tail > 1 && tail < 5) return few;
        return tail === 1 ? one : many;
    };

    /**
     * ⛔ ОДНО ЧИСЛО — ОДНО СЛОВО. `count` планировки это доступные КВАРТИРЫ, и
     * называть их «вариантами» в плитке сводки, «квартирами» в её подвале и
     * «квартирами» же в карточке планировки значило показывать одному человеку
     * три разных слова про одну и ту же величину. Хелпер объявлен здесь, у
     * pluralRu, а не рядом с первым вызовом: им пользуются и сводка, и панель
     * раскрытия, и своя копия в каждой разошлась бы.
     */
    const flatsWord = n => pluralRu(n, 'квартира', 'квартиры', 'квартир');

    const num = v => {
        const n = parseFloat(v);
        return isFinite(n) ? n : null;
    };

    /**
     * Идентичность карточки планировки = комнатность + диапазон площадей.
     * plan_id НЕ годится: он порядковый внутри ответа и между запросами
     * съезжает — по нему дедупликация и раскрытие 1.7.3 ловили бы чужую
     * планировку. Пустой ключ = битая запись, такую карточку не рисуем.
     */
    function planKey(p) {
        const rooms = parseInt(p.rooms, 10);
        const lo = num(p.area_min);
        if (!Number.isInteger(rooms) || rooms < 0 || lo === null || lo <= 0) return '';
        const hi = num(p.area_max);
        return rooms + '|' + lo + '|' + (hi === null ? lo : hi);
    }

    /** «42.2–43.8 м²», а у планировки с одной площадью — просто «42.2 м²». */
    function areaRangeText(p) {
        const lo = num(p.area_min);
        const hi = num(p.area_max);
        if (lo === null) return '';
        const loText = fmtArea(lo);
        if (hi === null || fmtArea(hi) === loText) return loText + ' м²';
        return loText + '–' + fmtArea(hi) + ' м²';
    }

    const planRoomsLabel = p => {
        const label = String(p.label == null ? '' : p.label).trim();
        return label !== '' ? label : roomsLabel(p.rooms);
    };

    /** Максимальная уценка внутри планировки — считает сервер, порог общий. */
    const planDiscountPct = p => {
        const pct = parseInt(p.discount_max_pct, 10) || 0;
        return pct >= DISCOUNT_MIN_PCT ? pct : 0;
    };

    /**
     * Обложка чертежа планировки. Класс — параметром: ровно эта же обложка
     * стоит в плитке раскрытой строки сводки (`fs-card__img`), и её отдельная
     * копия жила здесь же дословно. ⛔ Копий не заводим: у обложки четыре
     * тонких правила разом (safeSrc → прокси → ловля заглушки img-thumb 1×1 →
     * порядок «атрибуты до src»), и починка одной копии не доезжала бы до
     * второй — а именно так теряются уже закрытые мины.
     *
     * @param {Object} p   планировка из ответа API
     * @param {string} cls базовый класс обложки (модификатор — `cls + '--empty'`)
     */
    function planCover(p, cls) {
        const base = cls || 'jk-filter__plan-cover';
        const planSrc = safeSrc(p.layout_url);
        const renderSrc = planSrc === '' ? safeSrc(p.card_render_url) : '';
        const src = planSrc || renderSrc;
        const box = el('span', src === '' ? base + ' ' + base + '--empty' : base);
        if (renderSrc) box.classList.add('jk-filter__render-cover');
        if (src === '') {
            box.appendChild(icon('fas fa-drafting-compass'));
            return box;
        }
        const img = document.createElement('img');
        // ⛔ ПОРЯДОК: сначала атрибуты и обработчики, src — ПОСЛЕДНИМ. Присвоение
        // src немедленно ставит картинку в очередь загрузки, и `loading=lazy`,
        // выставленный после него, уже ничего не откладывает: браузер тянул ВСЕ
        // плитки разом, даже те, что за экраном. То же и с coverFallback —
        // подписаться на error надо ДО старта, иначе у картинки из кэша событие
        // успевает прийти раньше обработчика.
        img.setAttribute('loading', 'lazy');
        img.setAttribute('decoding', 'async');
        img.alt = renderSrc ? 'Рендер ЖК' : 'Планировка ' + planRoomsLabel(p) + ', ' + areaRangeText(p);
        coverFallback(box, img, base + '--empty', 'fas fa-drafting-compass');
        // Проверка безопасности (safeSrc) прошла выше, здесь только сжатие:
        // w=400 — бокс 200px на экране с ×2.
        img.src = thumbUrl(src, PLAN_COVER_WIDTH);
        box.appendChild(img);
        if (renderSrc) box.appendChild(el('span', 'jk-filter__render-caption', 'Рендер ЖК'));
        return box;
    }

    /**
     * Набор `data-zpo-*` для окна крупного плана (js/plan-modal.js).
     *
     * ⛔ ОДНА ФУНКЦИЯ НА ДВА МЕСТА. Атрибуты кладут и плитка «Сводки»
     *    (`summaryTile`), и обложка карточки планировок (`planCard`) — раньше
     *    список из четырнадцати полей жил копией в первом. Две копии одного
     *    набора расходятся при первой же правке, и разойтись они могли молча:
     *    окно просто не напечатало бы этаж или цену.
     *
     * ⛔ ЧИСЛА КЛАДЁМ АТРИБУТАМИ, А НЕ ТЕКСТОМ. Окно читает `data-zpo-*`; разбирать
     *    «20,7–24,1 м²» назад в числа значит завести второй формат одних и тех же
     *    величин, и он разойдётся с первым на первой же планировке без цены.
     *
     * ⛔ АДРЕС ВЫДАЧИ — ГОТОВЫЙ, ИЗ АТРИБУТА, И ОБЯЗАТЕЛЬНО СВОЙ. Окно ведёт
     *    «Смотреть N квартир» на страницу квартир ЭТОГО дома; взять там
     *    `location.pathname`, как на витрине, значило бы вести с карточки ЖК
     *    обратно на карточку ЖК. Атрибут ставим ВСЕГДА, даже пустым: по его
     *    наличию окно и понимает, что оно не на витрине, и при пустом адресе
     *    просто не рисует ссылку вместо того, чтобы вести в никуда.
     *
     * ⛔ НАЗВАНИЕ ЖК В ОКНО НЕ КЛАДЁМ. На карточке дома оно и так в шапке, а
     *    брать его из соседней разметки — тот же приём, на котором уже обожглись
     *    с кадром дома (см. heroShot): чужой узел рисуется позже.
     */
    function zpoData(node, p) {
        const host = document.getElementById('jkFilter');
        node.dataset.zpoBase = host ? (host.getAttribute('data-flats-url') || '') : '';
        const bid = host ? parseInt(host.getAttribute('data-building-id'), 10) : 0;
        if (bid > 0) node.dataset.zpoJkId = String(bid);
        const layout = safeSrc(p.layout_url);
        if (layout !== '') node.dataset.zpoSrc = layout;
        node.dataset.zpoLabel = planRoomsLabel(p);
        if (num(p.area_min) !== null) node.dataset.zpoAreaMin = String(num(p.area_min));
        if (num(p.area_max) !== null) node.dataset.zpoAreaMax = String(num(p.area_max));
        if (num(p.price_min) !== null) node.dataset.zpoPriceMin = String(num(p.price_min));
        if (num(p.price_max) !== null) node.dataset.zpoPriceMax = String(num(p.price_max));
        if (parseInt(p.floor_min, 10) > 0) node.dataset.zpoFloorMin = String(parseInt(p.floor_min, 10));
        if (parseInt(p.floor_max, 10) > 0) node.dataset.zpoFloorMax = String(parseInt(p.floor_max, 10));
        if (Number.isInteger(parseInt(p.rooms, 10))) node.dataset.zpoRooms = String(parseInt(p.rooms, 10));
        const cnt = parseInt(p.count, 10) || 0;
        if (cnt > 0) node.dataset.zpoCount = String(cnt);
        // Секции фида этой планировки — окно по ним спрашивает компактный генплан
        // страницы, есть ли корпуса на схеме («Посмотреть на генплане», 25.09.2026).
        const sections = Array.isArray(p.sections)
            ? p.sections.filter(s => typeof s === 'string' && s !== '').slice(0, 50) : [];
        if (sections.length > 0) node.dataset.zpoSections = JSON.stringify(sections);
    }

    /**
     * Кнопка, а не ссылка: у планировки нет своей страницы — клик разворачивает
     * список её квартир прямо здесь (1.7.3). Кнопка даёт клавиатуру и фокус
     * даром, а див с role=button пришлось бы обслуживать руками.
     */
    function planCard(p) {
        const key = planKey(p);
        if (key === '') throw new Error('планировка без комнатности/площади');
        const card = el('button', 'jk-filter__plan');
        card.type = 'button';
        card.dataset.planKey = key;
        card.dataset.rooms = String(parseInt(p.rooms, 10));
        card.dataset.areaMin = String(num(p.area_min));
        card.dataset.areaMax = String(num(p.area_max) === null ? num(p.area_min) : num(p.area_max));
        card.dataset.ym = EXPAND_SUPPORTED ? YM_EXPAND : YM_CARD;
        card.dataset.planLabel = planRoomsLabel(p);
        // Чертёж и «от N ₽» кладём на саму карточку: раскрытие показывает их
        // КРУПНО, и брать их неоткуда больше — в событии клика их нет, а второй
        // запрос за уже приехавшими данными был бы чистой тратой ведра 120/60.
        // Адрес уже прошёл safeSrc, поэтому в панели остаётся только сжать его.
        card.dataset.planSrc = safeSrc(p.layout_url);
        card.dataset.planPrice = String(parseInt(p.price_min, 10) || 0);
        // Кнопка-раскрытие: скринридер обязан слышать состояние ДО клика.
        if (EXPAND_SUPPORTED) card.setAttribute('aria-expanded', 'false');

        const cover = planCover(p);
        /**
         * ⛔ КЛИК ПО ЧЕРТЕЖУ ОТКРЫВАЕТ ОКНО КРУПНОГО ПЛАНА, а не панель квартир
         *    (вид, принятый владельцем 28.08.2026). Клик по ОСТАЛЬНОЙ карточке
         *    по-прежнему раскрывает панель — см. onCardClick. Одно нажатие
         *    никогда не открывает две вещи: onCardClick первой строкой выходит,
         *    если попали в обложку, и событие спокойно доходит до делегата
         *    js/plan-modal.js на документе.
         *
         * ⛔ Обложка — НЕ отдельная кнопка: она лежит внутри `<button>` карточки,
         *    а вложенная кнопка недопустима по разметке и ломает клавиатуру.
         *    Поэтому окно открывается делегатом по классу, а с клавиатуры
         *    карточка по-прежнему раскрывает панель — Enter на кнопке.
         */
        zpoData(cover, p);
        cover.appendChild(el('span', 'jk-filter__plan-tag', planRoomsLabel(p)));
        const pct = planDiscountPct(p);
        if (pct > 0) cover.appendChild(el('span', 'jk-filter__plan-badge', '−' + pct + '%'));
        // ⛔ КУСОК РЕНДЕРА ДОМА РЯДОМ С ЧЕРТЕЖОМ — вид принятого владельцем
        //    черновика и его же просьба дословно: «там должен быть рендер и
        //    планировка внутри карточки, так красивей смотрится». Чертёж сам по
        //    себе — техническая схема; кусок фасада даёт узнать дом.
        //    Адрес кадра ОДИН на всю выдачу, поэтому все карточки берут его из
        //    кэша браузера: двадцать планировок стоят одной загрузки.
        //
        // ⛔ КЛАДЁМ РЯДОМ С ОБЛОЖКОЙ, А НЕ ВНУТРЬ НЕЁ. Внутри уже живёт защита
        //    «битый чертёж → иконка-заглушка»: она ищет картинку и иконку прямо
        //    в обложке. Чужая картинка там перехватывала бы проверку на себя, а
        //    подпись тегом <i> читалась бы как та самая иконка — проверка живого
        //    чертежа падала на исправном коде (поймано гейтом 19.08).
        const hero = p.card_render_url && safeSrc(p.layout_url) === '' ? '' : heroShot();
        const duo = el('span', 'jk-filter__plan-duo');
        if (hero !== '') {
            const bg = document.createElement('img');
            bg.className = 'jk-filter__plan-bg';
            bg.setAttribute('loading', 'lazy');
            bg.setAttribute('decoding', 'async');
            bg.alt = '';
            bg.src = hero;
            duo.appendChild(bg);
            duo.classList.add('jk-filter__plan-duo--with-hero');
        }
        duo.appendChild(cover);
        // Полоска «КОРПУС · КОМНАТ · ПЛОЩАДЬ · ЭТАЖ» поверх низа кадра — оттуда же.
        // Пустое поле показываем прочерком, а не выдуманным значением.
        // ⛔ Значение — <span>, не <i>: <i> в этом коде означает иконку.
        const meta = el('span', 'jk-filter__plan-meta');
        [
            ['КОРПУС', planSectionText(p)],
            ['КОМНАТ', parseInt(p.rooms, 10) === 0 ? 'ст' : String(parseInt(p.rooms, 10) || '—')],
            ['ПЛОЩАДЬ', (parseFloat(p.area_min) || 0) > 0 ? fmtArea(parseFloat(p.area_min)).replace('.', ',') : '—'],
            ['ЭТАЖ', planFloorsText(p)],
        ].forEach(pair => {
            const b = el('b', '', pair[0]);
            b.appendChild(el('span', 'jk-filter__plan-meta-v', pair[1]));
            meta.appendChild(b);
        });
        duo.appendChild(meta);
        card.appendChild(duo);

        const body = el('span', 'jk-filter__plan-body');
        const row = el('span', 'jk-filter__plan-row');
        row.appendChild(el('span', 'jk-filter__plan-area', areaRangeText(p)));
        // ⛔ ДИАПАЗОН, А НЕ «ОТ». В черновике цена стоит «3,3–5,0 млн ₽»: у одной
        //    планировки цена гуляет по этажам, и одно «от» скрывает вторую границу.
        //    ⛔ И НОЛЬ — НЕ ЦЕНА: планировка без цены выходила как «0,0 млн ₽», то
        //    есть обещала бесплатную квартиру (живой прод 19.08, ЖК «ГРЕЙД»).
        const priceTxt = planPriceRange(p);
        if (priceTxt !== '') row.appendChild(el('span', 'jk-filter__plan-price', priceTxt));
        body.appendChild(row);

        const count = parseInt(p.count, 10) || 0;
        card.dataset.count = String(count);
        if (count > 0) {
            body.appendChild(el('span', 'jk-filter__plan-count',
                count + ' ' + pluralRu(count, 'квартира', 'квартиры', 'квартир')));
        }
        card.appendChild(body);

        // Скринридер обязан понимать, что кнопка не «открыть», а «показать
        // квартиры этой планировки» — иначе она звучит как ссылка в никуда.
        card.setAttribute('aria-label',
            'Планировка ' + planRoomsLabel(p) + ', ' + areaRangeText(p) +
            (count > 0 ? ', ' + count + ' ' + pluralRu(count, 'квартира', 'квартиры', 'квартир') : '') +
            ' — показать квартиры');
        return card;
    }

    // ═══ Сводка по комнатности (четвёртый вид) ═══
    /**
     * Зачем вид нужен. Иначе блок открывался бы «Планировками» — это два десятка
     * чертежей сразу. Сводка при открытии не грузит НИ ОДНОЙ картинки: только
     * строки по комнатности с площадью «от», ценой и остатком. Чертежи
     * появляются лишь когда человек раскрыл строку, и то порциями.
     * ⛔ Именно поэтому она СТАРТОВАЯ (DEFAULT_VIEW): видом, до которого надо
     * дотянуться кнопкой, картинок на открытии не убавляется ни у кого.
     *
     * ⛔ ДАННЫЕ — ТЕ ЖЕ, ЧТО У ФИЛЬТРА. Ни своего fetch, ни своей ручки: `items` в
     * этом режиме уже несут floorplans (modeOf('summary') === 'plans'). Второй
     * список с тем же смыслом разошёлся бы с первым — на этом уже обжигались
     * (счётчик галереи, «два списка с одним смыслом расходятся всегда»).
     * Поэтому же сводка перерисовывается на КАЖДЫЙ ответ фильтра сама, через
     * общий repaint(), и не может отстать от выбранных фильтров.
     *
     * ⛔ СЕРВЕРНУЮ ТАБЛИЦУ ТИПОВ НИКТО НЕ ГАСИТ СТИЛЕМ. Её снимает штатный
     * dropSsr() в onResults — то есть ровно тогда, когда данные УЖЕ приехали.
     * Правило вида `display:none` на .jk-filter__results прятало бы её и от бота,
     * и от человека без JS, причём навсегда при любом отказе.
     */

    /**
     * Планировки → строки по комнатности.
     *
     * ⛔ СЧЁТЧИК БЕРЁМ КАК ЕСТЬ. Здесь напрашивается `+p.count || 1` («ну хоть
     * одна-то есть»), и это враньё: ноль доступных квартир — это ноль, а не
     * единица, и сумма по строкам обязана сойтись с числом у фильтра.
     * ⛔ ЦЕНА И ПЛОЩАДЬ — ЧЕРЕЗ num(): у комнатности, где всё забронировано,
     * `price_min` приходит null, и Math.min по такому набору даёт Infinity.
     * На экране это выглядело как «Infinity млн ₽» и «не число ₽/м²».
     * Поэтому «нет значения» тут именно null, а не бесконечность.
     *
     * ⛔⛔ ₽/м² СЧИТАЕТСЯ ВНУТРИ ОДНОЙ ПЛАНИРОВКИ, а не делением двух минимумов
     * по всей комнатности. Здесь стояло `priceMin / areaMin`, и это цена
     * НЕСУЩЕСТВУЮЩЕЙ квартиры: самая дешёвая и самая маленькая — разные лоты.
     * Замер на живых данных прода 18.08: студии VEER показывали 760 750 ₽/м²
     * при настоящем минимуме 545 882 (+39 %), 1-комн VEER 608 980 против
     * 425 000 (+43 %), City Bay 1-комн 557 346 против 330 685 (+69 %),
     * «Самолёт 6» студии 210 333 против 137 592 (+53 %). У студий VEER это
     * почти МАКСИМУМ по дому — в строке с меткой «дешевле всего».
     * Настоящее число = минимум по планировкам, у каждой своя пара цена/площадь.
     *
     * ⛔ ПАРА БЕРЁТСЯ У КЛАСТЕРА, А НЕ У КВАРТИРЫ. Элемент `list` — это уже
     * склеенная планировка: сервер объединяет квартиры, у которых площадь
     * отличается не больше чем на 0,5 м² (FloorplanGrouper::TOLERANCE_M2), и
     * отдаёт минимальные цену и площадь ПО КЛАСТЕРУ. Поэтому на экране у студий
     * VEER стоит 545 882, а не 544 171: 544 171 — минимум по ОТДЕЛЬНОЙ квартире,
     * до склейки. Разница 0,3 % — против 24-69 % у старой формулы.
     */
    function summaryGroups(list) {
        const by = new Map();
        (Array.isArray(list) ? list : []).forEach(p => {
            const rooms = parseInt(p.rooms, 10);
            if (!Number.isInteger(rooms) || rooms < 0) return;
            let g = by.get(rooms);
            if (!g) {
                g = {
                    rooms: rooms, label: planRoomsLabel(p), count: 0, withPlan: 0,
                    areaMin: null, priceMin: null, priceMax: null, m2Min: null, plans: [],
                };
                by.set(rooms, g);
            }
            const count = parseInt(p.count, 10) || 0;
            g.count += count;
            if (safeSrc(p.layout_url) !== '') g.withPlan += count;
            const area = num(p.area_min);
            if (area !== null && area > 0 && (g.areaMin === null || area < g.areaMin)) g.areaMin = area;
            const price = num(p.price_min);
            if (price !== null && price > 0 && (g.priceMin === null || price < g.priceMin)) g.priceMin = price;
            // Верхняя граница: у планировки её может не быть вовсе (одна цена) —
            // тогда потолком служит её же `price_min`, иначе диапазон комнатности
            // молча обрывался бы на самой дорогой ПЛАНИРОВКЕ С РАЗБРОСОМ.
            const priceHi = num(p.price_max);
            const hi = priceHi !== null && priceHi > 0 ? priceHi : price;
            if (hi !== null && hi > 0 && (g.priceMax === null || hi > g.priceMax)) g.priceMax = hi;
            // Пара цена/площадь берётся у ОДНОЙ И ТОЙ ЖЕ планировки — см. блок
            // выше. isFinite: деление на площадь-ноль отсечено условием, но
            // мусор из фида (строка «—» в price_min) num() уже вернул бы null.
            if (price !== null && price > 0 && area !== null && area > 0) {
                const m2 = price / area;
                if (isFinite(m2) && m2 > 0 && (g.m2Min === null || m2 < g.m2Min)) g.m2Min = m2;
            }
            g.plans.push(p);
        });
        return Array.from(by.values()).sort((a, b) => a.rooms - b.rooms);
    }

    /** Цена «от» строки. Нет ни одной цены — так и говорим, а не рисуем пустоту. */
    const summaryPriceText = g => (g.priceMin === null ? 'Нет в продаже' : 'от ' + fmtPrice(g.priceMin));

    /* ⛔ Прежняя запись здесь объявляла плитку раскрытой строки «справочной, без
       ссылки»: своей страницы у планировки нет, а вести все плитки на один адрес
       каталога значит обещать переход, которого нет. Первая половина верна и
       сейчас — ССЫЛКИ у плитки по-прежнему нет. Вторая устарела 21.08: плитка
       стала кнопкой, открывающей окно крупного плана (см. `summaryTile`), и уже
       ИЗ ОКНА уводит адресная ссылка с фильтром по этой планировке. */

    /**
     * Кадр дома для левой половины плитки. Один на всю выдачу — берётся из
     * атрибута контейнера фильтра.
     *
     * ⛔ НЕ ИЗ ЧУЖОЙ РАЗМЕТКИ. Принятый черновик читал `.jk-about__hero img` —
     *    соседний блок, который рисуется ПОСЛЕ ответа API. На момент отрисовки
     *    плиток его может ещё не быть, и левая половина выходила бы пустой без
     *    всякой закономерности: то есть, то нет.
     */
    function heroShot() {
        const host = document.getElementById('jkFilter');
        const src = host ? (host.getAttribute('data-hero') || '') : '';
        return safeSrc(src);
    }

    /**
     * Этажи планировки одной строкой: «4–18» либо «4», либо прочерк.
     *
     * ⛔ ГРАНИЦЫ СЧИТАЕТ СЕРВЕР. Считать их здесь не из чего: витрине планировок
     *    приходит ЛЁГКАЯ карточка кластера, без списка квартир — он весил 99.7 %
     *    ответа. Поэтому сервер кладёт два скаляра, а не массив.
     */
    function planFloorsText(p) {
        const lo = parseInt(p.floor_min, 10);
        const hi = parseInt(p.floor_max, 10);
        if (!(lo > 0)) return '—';
        if (!(hi > 0) || hi === lo) return String(lo);
        return lo + '–' + hi;
    }

    /**
     * Корпус планировки. Пусто — прочерк, а не выдуманное «Литер 1».
     *
     * ⛔ Сервер отдаёт имя, только если корпус у кластера ОДИН: планировка,
     *    встречающаяся в двух корпусах, не принадлежит ни одному.
     */
    function planSectionText(p) {
        // section_label: один корпус — его имя; несколько — «Литер 1–4» или «9 корпусов».
        const name = String(p.section_label || p.section_name || '').trim();
        return name === '' ? '—' : name.slice(0, 12);
    }

    /**
     * Цена плитки диапазоном: «3,3–5,0 млн ₽».
     *
     * ⛔ НОЛЬ — ЭТО НЕ ЦЕНА. Планировка без цены выходила в витрину как
     *    «0,0 млн ₽», то есть обещала бесплатную квартиру (найдено на живом
     *    проде 19.08.2026, ЖК «ГРЕЙД», «Студия, 27 м²»). Нет числа — говорим
     *    словами, а не нулём.
     */
    function planPriceRange(p) {
        const lo = parseInt(p.price_min, 10) || 0;
        const hi = parseInt(p.price_max, 10) || lo;
        if (lo <= 0) return 'Цену уточняйте';
        const mln = v => {
            const m = v / 1e6;
            return (m >= 100 ? String(Math.round(m)) : m.toFixed(1)).replace('.', ',');
        };
        return (lo === hi ? mln(lo) : mln(lo) + '–' + mln(hi)) + ' млн ₽';
    }

    /**
     * Маленькая карточка планировки в сетке «Сводки» — вид принятого владельцем
     * черновика (`.fs-pc`): слева фото рендера дома, справа планировка, под ними
     * полоска «КОРПУС · КОМНАТ · ПЛОЩАДЬ · ЭТАЖ», заголовок, диапазон цен и
     * сколько предложений.
     *
     * ⛔ ДВЕ КАРТИНКИ В ОДНОЙ КАРТОЧКЕ — просьба владельца дословно: «там должен
     *    быть рендер и планировка внутри карточки, так красивей смотрится».
     * ⛔ Кадр дома у всех карточек ОДИН адрес: браузер берёт его из кэша, то есть
     *    вся сетка стоит одной загрузки, а не по загрузке на карточку.
     */
    function planTile(p) {
        // ⛔ АДРЕС БЕРЁМ ГОТОВЫЙ, А НЕ СКЛЕИВАЕМ. Черновик писал
        //    `/flats.php?building_id=N&rooms=R` — старый адрес, отдающий 301:
        //    страница квартир ЖК с 14.08.2026 живёт на своём ЧПУ, и каждый такой
        //    переход стоил бы краулеру лишний хоп при впритык бюджете обхода.
        const host = document.getElementById('jkFilter');
        const flatsUrl = host ? (host.getAttribute('data-flats-url') || '') : '';
        const tile = el(flatsUrl === '' ? 'div' : 'a', 'fs-pc');
        if (flatsUrl !== '') tile.href = flatsUrl;

        const imgBox = el('span', 'fs-pc__img');
        const hero = heroShot();
        if (hero !== '') {
            const bg = document.createElement('img');
            bg.className = 'fs-pc__bg';
            bg.setAttribute('loading', 'lazy');
            bg.setAttribute('decoding', 'async');
            bg.alt = '';
            bg.src = hero;
            imgBox.appendChild(bg);
        } else {
            imgBox.classList.add('fs-pc__img--plan-only');
        }
        const plan = safeSrc(p.layout_url);
        const render = plan === '' ? safeSrc(p.card_render_url) : '';
        if (render !== '') {
            const image = document.createElement('img');
            image.className = 'fs-pc__bg';
            image.alt = 'Рендер ЖК';
            image.loading = 'lazy';
            image.src = thumbUrl(render, PLAN_TILE_WIDTH);
            imgBox.appendChild(image);
            imgBox.appendChild(el('span', 'fs-pc__noplan', 'Рендер ЖК'));
        } else if (plan === '') {
            imgBox.appendChild(el('span', 'fs-pc__noplan', 'планировки нет'));
        } else {
            const pi = document.createElement('img');
            pi.className = 'fs-pc__plan';
            pi.setAttribute('loading', 'lazy');
            pi.setAttribute('decoding', 'async');
            pi.alt = 'Планировка ' + planRoomsLabel(p) + ', ' + areaRangeText(p);
            pi.src = thumbUrl(plan, PLAN_TILE_WIDTH);
            imgBox.appendChild(pi);
        }

        // ⛔ Значение — <span>, не <i>: <i> в этом коде означает иконку.
        const meta = el('span', 'fs-pc__meta');
        [
            ['КОРПУС', planSectionText(p)],
            ['КОМНАТ', parseInt(p.rooms, 10) === 0 ? 'ст' : String(parseInt(p.rooms, 10) || '—')],
            ['ПЛОЩАДЬ', (parseFloat(p.area_min) || 0) > 0 ? fmtArea(parseFloat(p.area_min)).replace('.', ',') : '—'],
            ['ЭТАЖ', planFloorsText(p)],
        ].forEach(pair => {
            const b = el('b', '', pair[0]);
            b.appendChild(el('span', 'fs-pc__meta-v', pair[1]));
            meta.appendChild(b);
        });
        imgBox.appendChild(meta);
        tile.appendChild(imgBox);

        // ⛔ «Студия. кв. 23,6 м²» читается нелепо: у студии нет комнат, и «кв.»
        //    после неё лишнее. Формулировка студии — своя (из черновика).
        // ⛔ ЕДИНИЦУ НЕ ДОБАВЛЯЕМ: `areaRangeText` уже возвращает её сам. Приписав
        //    вторую, получили «Студия, 20.7–21.1 м² м²» на живом проде (19.08.2026).
        // ⛔ И ЗАПЯТАЯ, А НЕ ТОЧКА: во всей витрине дробная часть через запятую,
        //    «20.7» рядом с «3,3–5,0 млн ₽» читается как чужая строка.
        const area = areaRangeText(p).replace(/(\d)\.(\d)/g, '$1,$2');
        tile.appendChild(el('span', 'fs-pc__t', parseInt(p.rooms, 10) === 0
            ? 'Студия' + (area === '' ? '' : ', ' + area)
            : planRoomsLabel(p) + (area === '' ? '' : ', ' + area)));
        tile.appendChild(el('span', 'fs-pc__p', planPriceRange(p)));
        const count = parseInt(p.count, 10) || 0;
        tile.appendChild(el('span', 'fs-pc__n',
            count + ' ' + pluralRu(count, 'предложение', 'предложения', 'предложений')));
        return tile;
    }

    /**
     * Плитка планировки внутри раскрытой строки «Сводки».
     *
     * ⛔ ПЛИТКА — КНОПКА, А НЕ КАРТИНКА. С 18.08 (10a621a07) она рисовалась
     *    инертной: ни ссылки, ни `role`, ни обработчика — человек раскрывал
     *    строку, видел чертёж и упирался в стену. Обработчика у неё не было
     *    НИКОГДА (`onCardClick` первой строкой выходит при `view === 'summary'`),
     *    то есть это не регресс, а недоделка. Клик открывает окно крупного
     *    плана — js/plan-modal.js, тот же самый файл, что на витрине квартир.
     *
     * ⛔ ДАННЫЕ КЛАДЁМ НА САМУ ПЛИТКУ, а не вычитываем обратно из её текста:
     *    разбирать «20,7–24,1 м²» назад в числа значит завести второй формат
     *    одних и тех же величин, и он разойдётся с первым на первой же
     *    планировке без цены. Набор `data-zpo-*` — тот же, что кладёт
     *    `planTile` витрины квартир (js/flats-showcase.js).
     */
    function summaryTile(p) {
        const tile = el('div', 'fs-card');
        tile.tabIndex = 0;
        tile.setAttribute('role', 'button');
        // ⛔ АДРЕС ВЫДАЧИ — ГОТОВЫЙ, ИЗ АТРИБУТА, И ОБЯЗАТЕЛЬНО СВОЙ. Окно ведёт
        //    «Смотреть N квартир» на страницу квартир ЭТОГО дома; взять там
        //    `location.pathname`, как на витрине, значило бы вести с карточки ЖК
        //    обратно на карточку ЖК. Атрибут ставим ВСЕГДА, даже пустым: по его
        //    наличию окно и понимает, что оно не на витрине, и при пустом адресе
        //    просто не рисует ссылку вместо того, чтобы вести в никуда.
        zpoData(tile, p);

        // Та же обложка, что у карточки третьего вида — только класс другой.
        tile.appendChild(planCover(p, 'fs-card__img'));
        // ⛔ ЗАПЯТАЯ, А НЕ ТОЧКА — ровно по той же причине, что у плитки третьего
        //    вида (`planTile`): во всей витрине дробная часть через запятую, и
        //    «20.7» рядом с «от 3 300 000 ₽» читается как чужая строка. Теперь
        //    это ещё и число, которое окно повторяет своим форматом.
        const area = areaRangeText(p).replace(/(\d)\.(\d)/g, '$1,$2');
        tile.appendChild(el('span', 'fs-card__a', area === '' ? '—' : area));
        const from = parseInt(p.price_min, 10) || 0;
        tile.appendChild(el('span', 'fs-card__p', from > 0 ? 'от ' + fmtPrice(from) : 'Нет в продаже'));
        const count = parseInt(p.count, 10) || 0;
        // ⛔ «Квартиры», а не «варианты»: это ТО ЖЕ ЧИСЛО, что в счётчике строки
        // и в подвале сводки (см. flatsWord).
        tile.appendChild(el('span', 'fs-card__n', count + ' ' + flatsWord(count)));
        if (count > 0) tile.dataset.zpoCount = String(count);
        tile.setAttribute('aria-label',
            'Планировка ' + planRoomsLabel(p) + (area === '' ? '' : ', ' + area)
            + ' — открыть крупный план');
        return tile;
    }

    /**
     * Дорисовать очередную порцию плиток раскрытой строки.
     *
     * ⛔ КАРТИНКИ СОЗДАЁМ ТОЛЬКО ЗДЕСЬ — то есть не раньше первого раскрытия.
     * В этом весь смысл вида: пока человек не попросил, сеть не трогаем совсем.
     * ⛔ И ТОЛЬКО ПОРЦИЮ: разом это 45 <img> у VEER 1-комн (замер 18.08), а тело
     * строки такую простыню ещё и обрезало.
     * try/catch на плитку — как у общей выдачи: одна битая запись фида не должна
     * оставить строку пустой.
     *
     * @param {HTMLElement} tiles сетка плиток внутри тела строки
     * @param {Object} g          группа комнатности
     * @param {Object} state      {shown:number} — сколько уже нарисовано
     * @param {number} limit      размер порции
     */
    function growSummaryTiles(tiles, g, state, limit) {
        // Кнопка стоит последней в сетке — снимаем её перед дорисовкой, иначе
        // новые плитки встали бы ПОСЛЕ неё.
        const oldBtn = tiles.querySelector('.fs-more');
        if (oldBtn) tiles.removeChild(oldBtn);

        const next = g.plans.slice(state.shown, state.shown + limit);
        const frag = document.createDocumentFragment();
        next.forEach(p => {
            try {
                frag.appendChild(summaryTile(p));
            } catch (e) {
                console.warn('jk-filter сводка: планировка пропущена —', e && e.message);
            }
        });
        state.shown += next.length;
        tiles.appendChild(frag);

        const left = g.plans.length - state.shown;
        if (left <= 0) return null;
        const btn = oldBtn || summaryMoreBtn(tiles, g, state);
        btn.textContent = 'Показать ещё (осталось ' + left + ')';
        btn.setAttribute('aria-label',
            'Показать ещё планировки ' + g.label + ' — осталось ' + left);
        tiles.appendChild(btn);
        return btn;
    }

    /**
     * Кнопка «показать ещё» ВНУТРИ тела строки. Ровно то же средство, что у вида
     * «Планировки» (moreBtn → growPlans): сети не касается, дорисовывает из уже
     * полученных данных.
     */
    function summaryMoreBtn(tiles, g, state) {
        const btn = el('button', 'fs-more');
        btn.type = 'button';
        btn.dataset.ym = 'jk_filter_summary_more';
        btn.addEventListener('click', ev => {
            // Кнопка лежит внутри тела строки, а тело — внутри строки, у которой
            // на голове свой обработчик. Голова кнопке не предок, но всплытие
            // всё равно гасим: сетка выдачи слушает клики на себе (onCardClick).
            ev.stopPropagation();
            const wasFocused = tiles.ownerDocument.activeElement === btn;
            const still = growSummaryTiles(tiles, g, state, SUMMARY_TILES_STEP);
            // Набор кончился — кнопка ушла из документа вместе с фокусом.
            // Уронить фокус на body значит заставить клавиатурного человека
            // начать обход страницы заново: возвращаем его на голову строки.
            // (Плитки теперь фокусируемы сами — но они ВЫШЕ ушедшей кнопки, и
            // прыжок вверх по списку читался бы как потеря места.)
            if (still === null && wasFocused) {
                const head = tiles.closest('.fs-row');
                const target = head && head.querySelector('.fs-row__head');
                if (target && typeof target.focus === 'function') target.focus();
            }
        });
        return btn;
    }

    function toggleSummaryRow(row, head, tiles, g, state) {
        const open = !row.classList.contains('is-open');
        row.classList.toggle('is-open', open);
        head.setAttribute('aria-expanded', open ? 'true' : 'false');
        // Делегат [data-ym] читает цель уже ПОСЛЕ нашего обработчика — значит
        // атрибут успевает описать именно то действие, которое клик выполнил.
        head.dataset.ym = open ? YM_SUM_EXPAND : YM_SUM_COLLAPSE;
        // Свернули и раскрыли снова — плитки уже нарисованы, второй раз их
        // создавать незачем (и картинки перезагружались бы).
        if (open && state.shown === 0) growSummaryTiles(tiles, g, state, SUMMARY_TILES_FIRST);
    }

    /** Строка сводки: тип · площадь «от» · доля · цена · счётчик · шеврон. */
    function summaryRow(g, index, maxCount, isCheapest) {
        const row = el('div', 'fs-row');
        const bodyId = SUMMARY_BODY_ID + index;

        const head = el('button', 'fs-row__head');
        head.type = 'button';
        head.dataset.ym = YM_SUM_EXPAND;
        // Аккордеон: скринридер обязан слышать состояние ДО клика и знать, что
        // именно кнопка открывает.
        head.setAttribute('aria-expanded', 'false');
        head.setAttribute('aria-controls', bodyId);

        const left = el('span', 'fs-row__l');
        const title = el('span', 'fs-row__t');
        title.appendChild(el('span', 'fs-row__t-text', g.label));
        // Метка у самого доступного типа — по цене входа, а не по площади:
        // человек выбирает, «с чего начать смотреть».
        if (isCheapest) title.appendChild(el('em', 'fs-tag', 'дешевле всего'));
        left.appendChild(title);
        left.appendChild(el('span', 'fs-row__a',
            g.areaMin === null ? '—' : 'от ' + fmtArea(g.areaMin) + ' м²'));
        const bar = el('span', 'fs-row__bar');
        const fill = el('i', 'fs-row__bar-fill');
        fill.setAttribute('aria-hidden', 'true');
        // CSSOM (element.style), а не инлайн-атрибут шаблона — CSP цела.
        const share = g.count > 0 && maxCount > 0
            ? Math.max(SUMMARY_BAR_MIN_PCT, Math.round(g.count / maxCount * 100))
            : 0;
        fill.style.width = share + '%';
        bar.appendChild(fill);
        left.appendChild(bar);
        head.appendChild(left);

        const right = el('span', 'fs-row__r');
        const price = el('span', 'fs-row__price', summaryPriceText(g));
        // Верхняя граница цены — ОТДЕЛЬНЫМ узлом, который CSS прячет на узком
        // экране: «от 6 100 000 ₽ · до 9 400 000 ₽» это 19 знаков в nowrap, и на
        // 390px они выдавливали левую колонку. В разметке узел есть всегда,
        // поэтому вид не «прыгает» при повороте экрана.
        if (g.priceMin !== null && g.priceMax !== null && g.priceMax > g.priceMin) {
            price.appendChild(el('span', 'fs-row__price-to', ' · до ' + fmtPrice(g.priceMax)));
        }
        right.appendChild(price);
        // ⛔ ₽/м² — МИНИМУМ ПО ПЛАНИРОВКАМ (summaryGroups.m2Min), а не частное
        // двух минимумов по комнатности: то была цена несуществующей квартиры,
        // завышенная на 24-69 % (замер в докблоке summaryGroups).
        const m2 = g.m2Min !== null ? Math.round(g.m2Min) : 0;
        if (m2 > 0) right.appendChild(el('span', 'fs-row__m2', nf.format(m2) + ' ₽/м²'));
        head.appendChild(right);

        head.appendChild(el('span', 'fs-row__n', String(g.count)));
        head.appendChild(icon('fas fa-chevron-down fs-row__c'));
        // ⛔ Доступное имя кнопки заканчивалось ГОЛЫМ ЧИСЛОМ («…152 000 ₽/м² 12»):
        // счётчик справа — единственный узел без единицы, и скринридер читал его
        // как хвост цены. Собираем имя явно.
        head.setAttribute('aria-label', summaryRowLabel(g, m2));
        row.appendChild(head);

        // Тело строки: внешний узел анимирует высоту (0fr→1fr в css), внутри —
        // сетка плиток. Раздельно, потому что анимируемый грид не может быть
        // одновременно сеткой плиток.
        const body = el('div', 'fs-row__body');
        body.id = bodyId;
        // ⛔ role=group, а не region: region — ЛАНДМАРК, и шесть комнатностей
        // давали скринридеру шесть пустых ландмарков в навигации по странице.
        body.setAttribute('role', 'group');
        body.setAttribute('aria-label', 'Планировки: ' + g.label);
        const inner = el('div', 'fs-row__body-in');
        /**
         * ⛔ ПОДБОР ОПРОСНИКА СУЖАЕТ ТОЛЬКО ПЛИТКИ, А НЕ СТРОКУ.
         * Счётчик строки (`g.count`), полоска доли и подвал сводки остаются
         * ровно теми же числами, что у фильтра («Найдено N планировок · M
         * квартир»). Второй счёт того же смысла разошёлся бы с первым — на
         * этом уже обжигались. Здесь величина ДРУГАЯ и названа своим именем:
         * сколько планировок этой комнатности подходит под ответы.
         */
        const fit = quizFitPlans(g.plans);
        const gv = fit === g.plans ? g : { label: g.label, plans: fit };
        if (fit !== g.plans) inner.appendChild(quizFitNote(fit.length, g.plans.length));
        const tiles = el('div', 'fs-row__tiles');
        inner.appendChild(tiles);
        body.appendChild(inner);
        row.appendChild(body);

        // Сколько плиток уже нарисовано. Живёт на замыкании строки, а не в
        // data-атрибуте: число, а не строка, и его нельзя подделать со стороны.
        const state = { shown: 0 };
        head.addEventListener('click', () => toggleSummaryRow(row, head, tiles, gv, state));
        // Опроснику нужно РАСКРЫТЬ подходящую строку, а не только показать её.
        // Реестр вместо поиска по DOM: раскрытие идёт тем же путём, что и клик
        // (toggleSummaryRow), поэтому порция плиток и aria-expanded не расходятся.
        summaryRows.push({
            rooms: g.rooms,
            node: row,
            open: () => {
                if (row.classList.contains('is-open')) return;
                toggleSummaryRow(row, head, tiles, gv, state);
            },
        });
        return row;
    }

    /**
     * Доступное имя строки сводки. Каждое число — со своей единицей: без этого
     * скринридер читал «Студии от 25.4 м² от 6 100 000 ₽ 152 000 ₽/м² 12».
     */
    function summaryRowLabel(g, m2) {
        const parts = [g.label];
        if (g.areaMin !== null) parts.push('от ' + fmtArea(g.areaMin) + ' м²');
        parts.push(summaryPriceText(g));
        if (g.priceMin !== null && g.priceMax !== null && g.priceMax > g.priceMin) {
            parts.push('до ' + fmtPrice(g.priceMax));
        }
        if (m2 > 0) parts.push(nf.format(m2) + ' ₽ за м²');
        parts.push(g.count + ' ' + flatsWord(g.count));
        return parts.join(', ');
    }

    /** Подвал: сколько типов вообще с чертежом. Ноль строк — молчим. */
    function summaryFoot(groups) {
        const total = groups.reduce((s, g) => s + g.count, 0);
        const withPlan = groups.reduce((s, g) => s + g.withPlan, 0);
        if (total <= 0) return null;
        const word = n => pluralRu(n, 'квартиры', 'квартир', 'квартир');
        return el('div', 'fs-foot', withPlan < total
            ? 'Планировка есть у ' + withPlan + ' ' + word(withPlan) + ' из ' + total + '.'
            : 'Планировка есть у всех ' + total + ' ' + word(total) + '.');
    }

    /**
     * Сообщение вместо сводки. role=status: человек уже переключил вид и ждёт
     * результата — молчащий пустой блок неотличим от «зависло».
     */
    function summaryNote(text) {
        const note = el('div', 'fs-note', text);
        note.setAttribute('role', 'status');
        return note;
    }

    /**
     * Маленькая плашка «Показать планировки» в шапке сводки — из принятого
     * владельцем черновика (`<label class="fs-tog">`).
     *
     * ⛔ ОНА НЕ УВОДИТ НА ДРУГОЙ ВИД. Раньше здесь стоял `setView('plans')`, то
     *    есть переключение на отдельную вкладку — и человек, нажав плашку внутри
     *    сводки, оказывался в другом разделе. Владелец: «не могу понять, теперь
     *    сводка в планировки ведёт». Теперь плашка раскрывает планировки ПРЯМО
     *    В СВОДКЕ, а отдельный вид «Планировки» живёт своей кнопкой сверху.
     *
     * ⛔ ДВА БЛОКА ПЛАНИРОВОК ЗДЕСЬ — РЕШЕНИЕ ВЛАДЕЛЬЦА, а не недосмотр: «у нас
     *    2 блока планировок должно было быть». Они разные по назначению: в сводке
     *    — быстрый просмотр по комнатности не выходя из обзора; отдельный вид —
     *    полноценный, с раскрытием квартир каждой планировки.
     */
    function summaryPlansToggle(onToggle) {
        const lab = el('label', 'fs-tog');
        lab.dataset.ym = YM_SUM_PLANS;
        const inp = document.createElement('input');
        inp.type = 'checkbox';
        inp.className = 'fs-tog__i';
        inp.addEventListener('change', () => {
            if (typeof onToggle === 'function') onToggle(inp.checked);
        });
        lab.appendChild(inp);
        lab.appendChild(el('span', 'fs-tog__t'));
        lab.appendChild(el('span', 'fs-tog__l', 'Показать планировки'));
        return lab;
    }

    /**
     * Маленький фильтр комнатности НАД сеткой карточек — из того же черновика.
     *
     * ⛔ ЭТО НЕ ДУБЛЬ ВЕРХНЕЙ ПАНЕЛИ. Чипы фильтра наверху меняют ЗАПРОС и всю
     *    выдачу; эти — только показанные карточки, не трогая ни запрос, ни числа
     *    в строках сводки. Разные предметы, потому и наборы разные: наверху
     *    «Студия 69» со счётчиком квартир, здесь «Студии» без числа — счёт стоит
     *    в каждой карточке своим («40 предложений»).
     */
    function summaryChips(groups, onPick) {
        const box = el('div', 'fs-chips');
        box.setAttribute('role', 'group');
        box.setAttribute('aria-label', 'Комнатность в сетке планировок');
        const mk = (label, rooms) => {
            const c = el('button', 'fs-chip', label);
            c.type = 'button';
            c.dataset.ym = 'jk_filter_summary_chip';
            c.setAttribute('aria-pressed', rooms === null ? 'true' : 'false');
            c.addEventListener('click', () => {
                box.querySelectorAll('.fs-chip').forEach(x => {
                    const on = x === c;
                    x.classList.toggle('is-on', on);
                    x.setAttribute('aria-pressed', on ? 'true' : 'false');
                });
                onPick(rooms);
            });
            return c;
        };
        const all = mk('Все', null);
        all.classList.add('is-on');
        box.appendChild(all);
        groups.forEach(g => box.appendChild(mk(g.label, g.rooms)));
        return box;
    }

    /**
     * Сетка карточек планировок внутри сводки.
     *
     * ⛔ РИСУЕТСЯ ТОЛЬКО КОГДА ПОКАЗАНА. До нажатия плашки контейнер скрыт, а у
     *    картинки внутри `display:none` ленивая загрузка не срабатывает НИКОГДА —
     *    это не «медленно», это «никогда». Поэтому карточки создаются в момент
     *    показа, а не заранее.
     * ⛔ И ПОРЦИЯМИ: разом это 45 чертежей у одной комнатности (замер 18.08) —
     *    столько никто не пролистает, а сеть тронута вся.
     */
    function summaryGrid(groups) {
        const wrap = el('div', 'fs-grid-wrap');
        wrap.hidden = true;
        const grid = el('div', 'fs-grid');
        const state = { rooms: null, shown: 0 };

        const plansFor = rooms => {
            const src = rooms === null
                ? groups.reduce((acc, g) => acc.concat(g.plans), [])
                : (groups.find(g => g.rooms === rooms) || { plans: [] }).plans;
            return quizFitPlans(src);
        };

        const draw = reset => {
            const plans = plansFor(state.rooms);
            if (reset) {
                grid.textContent = '';
                state.shown = 0;
            }
            const old = grid.querySelector('.fs-more');
            if (old) grid.removeChild(old);
            if (plans.length === 0) {
                grid.appendChild(summaryNote('Под этот выбор планировок нет.'));
                return;
            }
            const next = plans.slice(state.shown, state.shown + SUMMARY_TILES_FIRST);
            const frag = document.createDocumentFragment();
            next.forEach(pl => {
                try {
                    frag.appendChild(planTile(pl));
                } catch (e) {
                    console.warn('jk-filter сетка: планировка пропущена —', e && e.message);
                }
            });
            state.shown += next.length;
            grid.appendChild(frag);

            const left = plans.length - state.shown;
            if (left <= 0) return;
            const btn = el('button', 'fs-more');
            btn.type = 'button';
            btn.dataset.ym = 'jk_filter_summary_grid_more';
            btn.textContent = 'Показать ещё (осталось ' + left + ')';
            btn.addEventListener('click', ev => {
                ev.stopPropagation();
                draw(false);
            });
            grid.appendChild(btn);
        };

        wrap.appendChild(summaryChips(groups, rooms => {
            state.rooms = rooms;
            draw(true);
        }));
        wrap.appendChild(grid);
        return { node: wrap, draw: () => draw(true) };
    }

    function summaryPane(list) {
        // Реестр строк собирается заново на каждую отрисовку: старые узлы уже
        // не в документе, и раскрывать их означало бы работать с мусором.
        summaryRows = [];
        const pane = el('div', 'fs');
        const head = el('div', 'fs-head');
        head.appendChild(el('div', 'fs-head__t', 'Что есть в продаже'));
        pane.appendChild(head);

        const groups = summaryGroups(list);
        let maxCount = 0;
        let cheapest = null;
        groups.forEach(g => {
            if (g.count > maxCount) maxCount = g.count;
            if (g.priceMin !== null && (cheapest === null || g.priceMin < cheapest.priceMin)) cheapest = g;
        });
        // Метка «дешевле всего» имеет смысл только при выборе: у единственного
        // типа она сообщала бы, что он дешевле самого себя.
        if (groups.length < 2) cheapest = null;

        const rows = el('div', 'fs-rows');
        groups.forEach((g, i) => {
            const row = summaryRow(g, i, maxCount, g === cheapest);
            // Появление строк лесенкой — из принятого владельцем черновика
            // (FINAL-8901: `r.style.animationDelay = i*45 + 'ms'`). Взгляд
            // успевает пробежать сверху вниз, а не получает шесть строк одним
            // хлопком. Это CSSOM (element.style), а не инлайн-атрибут — CSP цела.
            // ⛔ Саму анимацию гасит prefers-reduced-motion в css; задержка на
            // погашенной анимации ничего не делает, поэтому здесь ветки нет.
            row.style.animationDelay = (i * SUMMARY_ROW_DELAY_MS) + 'ms';
            rows.appendChild(row);
        });
        pane.appendChild(rows);

        // ⛔ ПЛАШКА ПОКАЗЫВАЕТ СЕТКУ ВМЕСТО СТРОК — так в принятом черновике
        //    («образец ДомКлика»). Не раскрывает плитки внутри строк: при шести
        //    типах раскрытые строки давали простыню на три экрана, в которой
        //    ничего не найти.
        const grid = summaryGrid(groups);
        head.appendChild(summaryPlansToggle(on => {
            pane.classList.toggle('fs--plans', on);
            grid.node.hidden = !on;
            rows.hidden = on;
            // Рисуем в момент показа: до него контейнер скрыт, и чертежи внутри
            // него не начали бы грузиться вовсе.
            if (on) grid.draw();
            else pane.querySelectorAll('.fs-row.is-open').forEach(r => r.classList.remove('is-open'));
        }));
        pane.appendChild(grid.node);

        const foot = summaryFoot(groups);
        if (foot) pane.appendChild(foot);
        return pane;
    }

    // ═══ Опросник подбора (живёт ВНУТРИ панели фильтра) ═══
    /**
     * Что это. Одна полоса над управлением фильтра: три коротких вопроса, после
     * которых блок квартир показывает подходящие варианты. Принятый владельцем
     * черновик — `hero-draft-backup/FINAL-8901_2026-08-18_0008/flats-summary.js`,
     * функция quiz() (вид перенесён без изменений).
     *
     * ⛔ ПОЛОСА, А НЕ КАРТОЧКА. Просьба владельца дословно — «чтоб место не
     * занимал». Отдельный блок добавлял ~140px перед выдачей; строка внутри
     * фильтра занимает одну полосу и после ответа сворачивается в короткую
     * сводку выбора.
     *
     * ⛔ СВОЕГО ЗАПРОСА У ОПРОСНИКА НЕТ И БЫТЬ НЕ МОЖЕТ. Все варианты ответов
     * считаются из `floorplans`, которые фильтр УЖЕ загрузил для «Сводки» и
     * «Планировок». Отдельная ручка означала бы второй список с тем же смыслом
     * (и `view=list`, которого у flat_api.php нет вовсе: такой запрос падает в
     * тяжёлую ветку — замер 4,42 МБ / 2,05 с — и уходит в глобальное ведро
     * лимита 60/60 вместо ЖК-ведра 120/60).
     *
     * ⛔ КОМНАТНОСТЬ ПРИМЕНЯЕТСЯ ЧЕРЕЗ ПУБЛИЧНЫЙ ДОГОВОР `api.setRooms()`, а не
     * синтетическим кликом по чужому чипу. Клик по чипу — это чужой внутренний
     * обработчик: он держится ровно до первой перерисовки управления, и такая
     * связка ломается молча. Договор же возвращает «применено/нет», и по этому
     * ответу видно, ждать ли новую выдачу.
     *
     * ⛔ БЮДЖЕТ И СКИДКА В ПОЛЯ ФИЛЬТРА НЕ ПИШУТСЯ — публичного способа нет:
     * у `window.BroniJkFilter` (версия 5) из сеттеров только `setRooms` и
     * `setResultMode`. Писать в чужие поля и слать события бессмысленно: фильтр
     * владеет ими и переписывает при смене комнатности (черновик проверил это
     * пятью попытками записи подряд). Поэтому бюджет и скидка сужают ПЛИТКИ
     * раскрытой строки сводки, где данные наши, а счётчики фильтра остаются
     * нетронутыми. Чтобы они доезжали и до самого фильтра, нужен публичный
     * сеттер цены в jk-filter.js — см. отчёт блока.
     */

    // Границы корзин бюджета — трети по числу планировок. Не «до 5 / до 10 млн»
    // из шаблона: у ЖК на проде цены расходятся от 2,4 млн до 4,6 млрд, и любая
    // назначенная сумма была бы издевательством у одной половины домов и
    // бессмыслицей у другой.
    const QUIZ_LO = 1 / 3;
    const QUIZ_HI = 2 / 3;
    const YM_QUIZ_ANSWER = 'jk_filter_quiz_answer';
    const YM_QUIZ_BACK = 'jk_filter_quiz_back';
    const YM_QUIZ_AGAIN = 'jk_filter_quiz_again';
    const YM_SUM_PLANS = 'jk_filter_summary_plans';

    /** Ответы, которыми сужается показ. rooms применяет сам фильтр. */
    let quizPick = { rooms: null, budget: null, deadline: null, floor: null, renovation: null, discount: false };
    let quizDone = false;
    let quizScrolled = false;
    let quizApi = null;
    /**
     * Снимок планировок ВСЕГО дома — источник вариантов ответа.
     *
     * ⛔ НЕ ТЕКУЩАЯ ВЫДАЧА. Ответ «комнатность» сужает её через фильтр, и
     * следующий вопрос, посчитанный по сужённому набору, предложил бы бюджет
     * без остальных типов, а кнопка «назад» вернула бы человека к списку из
     * одного варианта — то есть в тупик.
     */
    let quizBase = [];
    /**
     * Комнатность, КОТОРОЙ ПРИНАДЛЕЖИТ нарисованный набор (из `payload.state` —
     * слепка фильтра НА МОМЕНТ ЗАПРОСА).
     * ⛔ Живое состояние фильтра для этого не годится: «заново» сбрасывает его
     * мгновенно, а на экране ещё висит прежний, СУЖЁННЫЙ набор — и снимок дома
     * для опросника снимался бы с остатка. Ровно на этом тест и поймал ошибку:
     * после «заново» первый вопрос предлагал одну комнатность из трёх.
     */
    let renderedRooms;
    /** Строки сводки на экране: {rooms, node, open()}. Заполняет summaryPane. */
    let summaryRows = [];

    /**
     * Границы цены планировки. У кластера с одной ценой верх равен низу —
     * иначе диапазон обрывался бы на самой дорогой планировке С РАЗБРОСОМ (та
     * же мина, что в summaryGroups).
     * null — цены нет вовсе (всё забронировано): такую под бюджет не подставить.
     */
    function planPrices(p) {
        const lo = num(p.price_min);
        if (lo === null || lo <= 0) return null;
        const hi = num(p.price_max);
        return { lo: lo, hi: hi !== null && hi > lo ? hi : lo };
    }

    /**
     * Сужает ли подбор показ СВОИМИ силами.
     *
     * ⛔ ЗДЕСЬ ОСТАЛАСЬ ОДНА СКИДКА — И ЭТО ПОЧИНКА, А НЕ УРЕЗАНИЕ. До версии 6
     * договора бюджет было некуда применить, и он отсеивал планировки прямо в
     * сводке. Теперь `api.setPrice()` кладёт его в поля «от-до» фильтра, то
     * есть отбор идёт на сервере и виден в «Найдено N». Оставить рядом ещё и
     * клиентский отсев по той же цене значило бы завести ВТОРОЙ механизм с тем
     * же смыслом — они разойдутся на первом же округлении шага слайдера
     * (PRICE_STEP = 50 000 ₽).
     * Скидка сюда не уехала: поля «только со скидкой» у фильтра нет вовсе.
     */
    function quizNarrows() {
        return quizDone && quizPick.discount;
    }

    /** Планировка годится, если у неё есть уценка выше общего порога. */
    function planFitsPick(p) {
        return planDiscountPct(p) > 0;
    }

    /**
     * ⛔ ЕСЛИ НЕ ПОДОШЛО НИЧЕГО — ПОКАЗЫВАЕМ ВСЁ. Пустая строка после опроса
     * читается как «у вас ничего нет», хотя квартиры есть, просто не под эти
     * условия. Возврат ТОГО ЖЕ массива (===) — признак «не сужали», по нему
     * строка понимает, нужна ли поясняющая надпись.
     */
    function quizFitPlans(list) {
        if (!quizNarrows() || !Array.isArray(list)) return list;
        const fit = list.filter(planFitsPick);
        return fit.length > 0 ? fit : list;
    }

    /**
     * Надпись о сужении. ⛔ Формулировка идёт за ФАКТИЧЕСКИМИ условиями: при
     * бюджете «от 755 млн» отсекаются те, что ДЕШЕВЛЕ, а не дороже, и общий
     * текст «дешевле вашего бюджета» врал бы ровно в половине случаев.
     */
    function quizFitNote(shown, total) {
        return el('div', 'fs-pick', 'Со скидкой ' + shown + ' '
            + pluralRu(shown, 'планировка', 'планировки', 'планировок')
            + ' из ' + total + '.');
    }

    /**
     * Короткая подпись суммы для кнопок опросника.
     *
     * ⛔ ЭТО НЕ ВТОРОЙ ФОРМАТ ЦЕНЫ. Настоящая цена везде печатается fmtPrice()
     * («6 100 000 ₽»), и опросник её нигде не показывает: у него на кнопках
     * ГРАНИЦЫ корзин («до 6,1 млн»), четыре штуки в одну строку. Полными
     * рублями это ~60 знаков — на 390px строка выдавливает горизонтальный
     * скролл всей страницы.
     *
     * ⛔ ТОЧНОСТЬ ИДЁТ ЗА РАЗБРОСОМ. Округление до миллионов схлопывало среднюю
     * корзину в «5 млн — 5 млн»: у студий весь разброс 5-6 млн, и обе границы
     * округлялись в одно число. Узкий разброс (и сумма меньше десяти миллионов,
     * где Math.round дал бы «0 млн») — показываем десятые.
     */
    function quizMoney(v, span) {
        if (v >= 1e9) return (v / 1e9).toFixed(1).replace('.', ',') + ' млрд';
        const m = v / 1e6;
        return (span < 1e7 || m < 10 ? m.toFixed(1).replace('.', ',') : String(Math.round(m))) + ' млн';
    }

    /**
     * Корзины бюджета ПО ЦЕНАМ ЭТОГО ЖК: трети по числу планировок выбранной
     * комнатности.
     *
     * ⛔ СЧИТАЕМ ПО УЖЕ ВЫБРАННОЙ КОМНАТНОСТИ, А НЕ ПО ВСЕМУ ДОМУ. Так и был
     * пойман главный дефект черновика: диапазоны строились по всему разбросу, и
     * после выбора «студии» человеку предлагалось «от 9 млн» там, где студии
     * стоят до 6. Числа настоящие, но не про то, что он выбрал.
     *
     * ⛔ ЕДИНИЦА СЧЁТА — ПЛАНИРОВКА, А НЕ КВАРТИРА, и это ограничение данных, а
     * не выбор: в ответе `view=plans` поштучных квартир нет вовсе (`floors[]`
     * там не передаётся), а просить их отдельно — тот самый второй запрос,
     * которого у опросника быть не должно.
     */
    function quizBudgets(list) {
        const ps = list.map(p => num(p.price_min))
            .filter(v => v !== null && v > 0)
            .sort((a, b) => a - b);
        // Меньше трёх цен — делить не на что, вопрос пропускаем целиком.
        if (ps.length < 3) return [];
        const at = k => ps[Math.min(ps.length - 1, Math.floor(ps.length * k))];
        const lo = at(QUIZ_LO);
        const hi = at(QUIZ_HI);
        const span = ps[ps.length - 1] - ps[0];
        const f = v => quizMoney(v, span);
        const out = [{ label: 'до ' + f(lo), budget: { max: lo } }];
        // Средняя корзина нужна, только если её границы РАЗЛИЧАЮТСЯ на вид:
        // кнопка «5 млн — 5 млн» это не выбор, а недоразумение.
        if (f(lo) !== f(hi)) out.push({ label: f(lo) + ' — ' + f(hi), budget: { min: lo, max: hi } });
        out.push({ label: 'от ' + f(hi), budget: { min: hi } });
        out.push({ label: 'Бюджет не важен' });
        return out;
    }

    /**
     * Варианты комнатности. Метки и счётчики — из той же группировки, что
     * рисует строки сводки, поэтому «2-комн. 12» на кнопке и «12» в строке это
     * одно число из одного места.
     * ⛔ Счётчик берём КАК ЕСТЬ: `+p.count || 1` превратил бы ноль доступных в
     * одну квартиру, и кнопка разошлась бы со строкой на том же экране.
     */
    function quizRoomsOpts(groups) {
        // Один тип — выбирать не из чего, вопрос ради вопроса.
        if (groups.length < 2) return [];
        return groups.map(g => ({ label: g.label, count: g.count, rooms: g.rooms }));
    }

    /**
     * ⛔ ВОПРОС ПРО СКИДКУ ПОЯВЛЯЕТСЯ, ТОЛЬКО ЕСЛИ СКИДКИ ЕСТЬ. Спросить «показать
     * со скидкой» там, где их ноль, значит подвести человека к пустому экрану.
     * Порог уценки — общий DISCOUNT_MIN_PCT (planDiscountPct), а не своя копия.
     */
    function quizDiscountOpts(list) {
        const cut = list.filter(p => planDiscountPct(p) > 0);
        const n = cut.reduce((s, p) => s + (parseInt(p.count, 10) || 0), 0);
        if (cut.length === 0 || n === 0) return [];
        return [
            { label: 'Да, со скидкой', count: n, discount: true },
            { label: 'Не важно' },
        ];
    }

    /**
     * Снимок фасетов ЖК — источник вариантов для срока и этажа.
     * ⛔ Своей копии не держим: фасеты живут у фильтра, и вторая копия
     * разошлась бы с плашками на экране. Геттер отдаёт клон, портить нечего.
     */
    function quizFacets() {
        try {
            const f = api.facets;
            return f && typeof f === 'object' ? f : null;
        } catch (e) {
            return null;
        }
    }

    /**
     * Варианты срока сдачи — ПОДПИСИ плашек самого фильтра.
     *
     * ⛔ ИНДЕКСЫ НАРУЖУ НЕ НОСИМ: набор и порядок сроков у каждого ЖК свои
     * («Сдан 2024» вторым у одного дома и «Строится · 3 кв. 2027» вторым у
     * другого), и запомненный индекс отфильтровал бы по чужому сроку. Договор
     * версии 6 принимает подпись — она же и на кнопке опроса.
     *
     * ⛔ ВОПРОС ПОЯВЛЯЕТСЯ ТОЛЬКО ТАМ, ГДЕ СРОК ВЫБИРАЕТСЯ. При
     * `sections_supported=false` срок один на всё здание и печатается фактом, а
     * не кнопкой — спрашивать про него значит спрашивать ни о чём. Один срок в
     * списке — то же самое.
     */
    function quizDeadlineOpts() {
        const f = quizFacets();
        if (!f || f.sections_supported !== true || !Array.isArray(f.deadlines)) return [];
        const list = f.deadlines
            .filter(d => d && String(d.label || '') !== '')
            .map(d => ({
                label: String(d.label),
                count: parseInt(d.count, 10) || 0,
                deadline: String(d.label),
            }));
        if (list.length < 2) return [];
        return list.concat([{ label: 'Не важно' }]);
    }

    /**
     * Варианты этажа — по РЕАЛЬНОЙ этажности дома из фасетов.
     *
     * ⛔ Предлагать «от 10-го» там, где всего пять этажей, значит обещать
     * несуществующее. Пороги привязаны к максимуму: «от 5-го» имеет смысл в
     * доме от восьми этажей, «от 10-го» — от шестнадцати.
     * ⛔ ВОПРОС С ОДНИМ ВАРИАНТОМ НЕ ЗАДАЁМ. У дома в один-два этажа (и у ЖК без
     * этажности вовсе — старый кэш фасетов поля `floor` не несёт) остаётся
     * только «Не важно», а это не выбор. Тот же приём, что при менее чем трёх
     * ценах у бюджета.
     */
    function quizFloorOpts() {
        const f = quizFacets();
        const floor = f && f.floor && typeof f.floor === 'object' ? f.floor : null;
        const lo = floor ? parseInt(floor.min, 10) || 0 : 0;
        const hi = floor ? parseInt(floor.max, 10) || 0 : 0;
        // Вырожденная этажность: слайдер этажа у такого ЖК скрыт целиком, и
        // setFloor честно вернёт false — вопрос был бы пустышкой.
        if (hi <= lo) return [];
        const out = [];
        if (lo <= 1) out.push({ label: 'Не первый', floor: { min: 2 } });
        if (hi >= 8) out.push({ label: 'От 5-го и выше', floor: { min: 5 } });
        if (hi >= 16) out.push({ label: 'От 10-го и выше', floor: { min: 10 } });
        if (out.length === 0) return [];
        return out.concat([{ label: 'Не важно' }]);
    }

    /**
     * Варианты отделки — бакеты самого фильтра (фасет `renovation`).
     *
     * ⛔ НОСИМ КЛЮЧ БАКЕТА, А НЕ ИНДЕКС И НЕ ПОДПИСЬ. Ключи ('none' / 'rough' /
     * 'whitebox' / 'fine') — серверный whitelist RenovationBucket: он один на все
     * витрины и не зависит от того, какие бакеты есть у ЭТОГО дома. Индекс
     * зависел бы («1» = «Черновая» у одного ЖК и «Предчистовая» у другого) —
     * ровно та мина, из-за которой срок сдачи ходит ПОДПИСЬЮ. Со сроком выбора
     * нет: стабильного ключа у него не существует; у отделки он есть — берём его.
     *
     * ⛔ ОДИН БАКЕТ — НЕ ВОПРОС. У ЖК, где вся отделка одинаковая, кнопка ничего
     * не отбирает, и такой шаг пропускается целиком (то же правило, что у одной
     * комнатности и у одного срока). «Не важно» в счёт вариантов не идёт.
     */
    function quizRenovationOpts() {
        const f = quizFacets();
        const list = f && Array.isArray(f.renovation) ? f.renovation : [];
        const out = list
            .filter(r => r && typeof r.key === 'string' && r.key !== ''
                && String(r.label || '') !== '' && (parseInt(r.count, 10) || 0) > 0)
            .map(r => ({
                label: String(r.label),
                count: parseInt(r.count, 10) || 0,
                renovation: String(r.key),
            }));
        if (out.length < 2) return [];
        return out.concat([{ label: 'Не важно' }]);
    }

    /**
     * Запомнить набор планировок как «весь дом».
     * ⛔ Не перетираем снимок, когда выдача уже сужена комнатностью — иначе
     * первый же ответ обнулил бы варианты следующих вопросов.
     */
    function quizRemember(list) {
        if (!Array.isArray(list) || list.length === 0) return;
        // Пустой снимок берём любым: у пришедшего по ссылке `#room-r2` фильтр
        // стартует уже с комнатностью, и без этой ветки опросника не было бы.
        if (renderedRooms === null || renderedRooms === undefined || quizBase.length === 0) {
            quizBase = list;
        }
    }

    /**
     * Фильтр стоит в дефолте (ни одного применённого условия)?
     * ⛔ Поля читаем ИЗ СЛЕПКА ЗАПРОСА `payload.state`, а не собираем свою
     * подпись: новое поле фильтра иначе молча выпало бы из проверки, и опрос
     * считал бы сброшенным то, что сброшено не полностью.
     *
     * ⛔ ЭТОТ СПИСОК ОБЯЗАН РАСТИ ВМЕСТЕ СО СНИМКОМ `getState()` фильтра. Пропуск
     * одного поля не виден глазом: опрос из одного вопроса (у ЖК с единственной
     * комнатностью и единственным сроком остаётся, например, только этаж) стирал
     * бы собственный ответ сразу после того, как его применили. Сторожат
     * поведение тесты `filterIsWideOpen` в tests/JS/jk-filter-quiz.test.js — по
     * тесту на каждое поле, чтобы диверсия «вырежи одну проверку» падала.
     */
    function filterIsWideOpen(state) {
        if (!state || typeof state !== 'object') return false;
        const empty = v => v === null || v === undefined;
        return empty(state.rooms) && empty(state.deadline)
            && empty(state.priceMin) && empty(state.priceMax)
            && empty(state.floorMin) && empty(state.floorMax)
            && empty(state.renovation);
    }

    /** Забыть ответы, не трогая фильтр (его уже сбросили без нас). */
    function quizForget() {
        quizPick = { rooms: null, budget: null, deadline: null, floor: null, renovation: null, discount: false };
        quizDone = false;
        quizScrolled = false;
        if (quizApi) quizApi.forget();
    }

    /** Раскрыть строку выбранной комнатности и один раз довести её до глаз. */
    function quizOpenRow() {
        if (summaryRows.length === 0) return;
        const hit = summaryRows.filter(r => r.rooms === quizPick.rooms);
        const target = hit.length > 0 ? hit[0] : summaryRows[0];
        target.open();
        if (quizScrolled) return;
        quizScrolled = true;
        if (typeof target.node.scrollIntoView !== 'function') return;
        target.node.scrollIntoView({ behavior: motionOff() ? 'auto' : 'smooth', block: 'center' });
    }

    /**
     * Попросить фильтр отобрать комнатность и показать найденное «Сводкой».
     *
     * Судьбу отрисовки решает repaint(): пока `quizDone`, он сам раскрывает
     * подходящую строку — и на ответе фильтра, и на локальной перерисовке.
     */
    function askFilter(pick) {
        const seq = repaintSeq;
        // ⛔ ВСЕ ОТВЕТЫ УХОДЯТ В ФИЛЬТР ОДНОЙ ПАЧКОЙ. Каждый сеттер планирует
        // запрос через общий debounce фильтра, а тот сбрасывает прежний таймер
        // (schedule в jk-filter.js) — значит четыре вызова подряд дают ОДИН
        // запрос, а не четыре. Замер это подтверждает: 1 обращение к flat_api
        // на весь опрос.
        // ⛔ КАЖДЫЙ СЕТТЕР ПРОВЕРЯЕМ ОТДЕЛЬНО (`typeof`), а не по `version`: у
        // человека в браузере может лежать закэшированный jk-filter.js версии
        // 5, и тогда доедет только комнатность, а остальное молча не применится
        // — но опрос не сломается и не соврёт.
        if (typeof api.setRooms === 'function') api.setRooms(pick.rooms);
        if (typeof api.setPrice === 'function') {
            const b = pick.budget;
            api.setPrice(b && b.min ? b.min : null, b && b.max ? b.max : null);
        }
        if (typeof api.setDeadline === 'function') api.setDeadline(pick.deadline);
        if (typeof api.setFloor === 'function') {
            const fl = pick.floor;
            api.setFloor(fl && fl.min ? fl.min : null, fl && fl.max ? fl.max : null);
        }
        // Отделка — договор версии 7. Тем же пакетом, что и остальные ответы:
        // debounce фильтра сбрасывает прежний таймер, поэтому запрос остаётся
        // ОДИН на весь опрос (свойство закреплено тестом).
        if (typeof api.setRenovation === 'function') api.setRenovation(pick.renovation);

        // Показывать найденное — дело «Сводки»: она рисует строки по
        // комнатности из того же набора, что фильтр уже загрузил.
        if (view !== 'summary' && VIEWS.indexOf('summary') >= 0) setView('summary');
        // ⛔ СУЖЕНИЕ ПОКАЗЫВАЕМ СРАЗУ, А НЕ ЖДЁМ СЕТЬ. Ответ фильтра приедет
        // через debounce плюс запрос, и всё это время экран стоял бы прежним —
        // человек решил бы, что кнопка не сработала. А сорвись запрос, бюджет
        // не применился бы вовсе. Перерисовка своя, из уже полученных данных:
        // ни запроса, ни места в ведре лимита. Придёт ответ фильтра — он
        // перерисует ещё раз и раскроет строку сам (см. repaint).
        if (repaintSeq !== seq) return;
        if (view === 'summary' && renderedMode === 'plans' && dom) repaint();
    }

    /**
     * Опросник: разметка, шаги, применение ответов.
     * Возвращает {node, refresh, setVisible} — каркас выдачи вешает узел внутрь
     * панели фильтра, а repaint() обновляет варианты на каждый новый набор.
     */
    function buildQuiz() {
        const box = el('div', 'fq jk-hidden');
        box.setAttribute('role', 'group');
        box.setAttribute('aria-label', 'Подбор квартиры');
        const line = el('div', 'fq__in');

        const back = el('button', 'fq__back jk-hidden');
        back.type = 'button';
        back.dataset.ym = YM_QUIZ_BACK;
        back.setAttribute('aria-label', 'Предыдущий вопрос');
        back.appendChild(icon('fas fa-arrow-left'));

        const ask = el('span', 'fq__q');
        // Шаг меняется без перезагрузки — иначе скринридер не узнаёт о новом
        // вопросе вовсе (фокус остаётся на кнопке, которой уже нет).
        ask.setAttribute('role', 'status');
        const opts = el('span', 'fq__opts');
        const done = el('span', 'fq__done jk-hidden');
        const dots = el('span', 'fq__dots');
        dots.setAttribute('aria-hidden', 'true');

        line.appendChild(back);
        line.appendChild(ask);
        line.appendChild(opts);
        line.appendChild(done);
        line.appendChild(dots);
        box.appendChild(line);

        const picked = {};
        let step = 0;
        // Ответы применены (фильтру уже сказали). Флаг нужен потому, что draw()
        // зовётся и на КАЖДЫЙ новый набор данных: без него завершённый опрос
        // слал бы фильтру запрос на каждый его же ответ — бесконечный круг.
        let applied = false;
        // ⛔ СПРАШИВАТЬ БЫЛО НЕЧЕГО — ОПРОСНИКА НЕТ ВОВСЕ. У ЖК с одной
        // комнатностью, без скидок и меньше чем с тремя ценами все три шага
        // пропускаются подряд, и опрос «завершался» с пустым «Подобрали:»,
        // да ещё и переключал вид. Такой ЖК просто не показывает полосу.
        let mute = false;

        /** Планировки под уже выбранную комнатность (или весь дом). */
        function scope() {
            const r = picked.rooms ? picked.rooms.rooms : null;
            if (r === null || r === undefined) return quizBase;
            const sub = quizBase.filter(p => parseInt(p.rooms, 10) === r);
            return sub.length > 0 ? sub : quizBase;
        }

        // ⛔ ОТДЕЛКА В ОПРОСЕ ЕСТЬ (приказ владельца 18.08.2026).
        //
        // Здесь стояло обратное утверждение — «отделки нет по решению владельца
        // 17.08, потому что поля отделки в ответе flat_api.php нет». НЕВЕРНЫ БЫЛИ
        // ОБА ПОЛОЖЕНИЯ: `renovation` фильтр принимает давно (flat_api.php →
        // ALLOWED_FILTERS → buildListWhere, whitelist RenovationBucket), а
        // `FlatHandler::list()` отдаёт и `renovation`, и `renovation_label`.
        // Не хватало ровно вариантов — их теперь даёт фасет `renovation`.
        // ⛔ Ложная ссылка на «решение владельца» опаснее самой пропажи: следующий
        // читатель поверил бы ей и не стал проверять. Если вопрос когда-нибудь
        // снова уберут — писать ПРИЧИНУ и дату замера, а не чужое решение.
        //
        // Порядок вопросов — тот, который назвал владелец: комнатность, бюджет,
        // год сдачи, этаж, скидка; отделка встала перед скидкой, чтобы взаимный
        // порядок пяти прежних не менялся. Каждый, кроме скидки, применяется
        // ПУБЛИЧНЫМ договором фильтра (отделка — версии 7) и виден в «Найдено N»;
        // скидка сужает плитки сводки, потому что такого поля у фильтра нет вовсе.
        // ⚠️ Плитки «Сводки» отделка НЕ сужает: в ответе `view=plans` поля отделки
        // нет (у кластера планировки квартиры бывают с РАЗНОЙ отделкой, и одно
        // значение там было бы враньём). Сужаются «Найдено N» и сам фильтр.
        // ⛔ Любой шаг, у которого нет вариантов, пропускается целиком — у ЖК с
        // одним сроком, без этажности или без скидок вопросов будет меньше пяти,
        // и это правильнее, чем кнопка, которая ничего не меняет.
        const steps = [
            {
                key: 'rooms',
                ask: 'Сколько комнат нужно?',
                opts: () => quizRoomsOpts(summaryGroups(quizBase)),
            },
            {
                key: 'budget',
                ask: 'На какой бюджет ориентируетесь?',
                opts: () => quizBudgets(scope()),
            },
            {
                key: 'deadline',
                ask: 'Когда нужен дом?',
                opts: quizDeadlineOpts,
            },
            {
                key: 'floor',
                ask: 'Этаж имеет значение?',
                opts: quizFloorOpts,
            },
            {
                key: 'renovation',
                ask: 'Какая нужна отделка?',
                opts: quizRenovationOpts,
            },
            {
                key: 'discount',
                ask: 'Показать только со скидкой?',
                opts: () => quizDiscountOpts(scope()),
            },
        ];

        function optionBtn(o, index) {
            const btn = el('button', 'fq__opt', o.label);
            btn.type = 'button';
            btn.dataset.ym = YM_QUIZ_ANSWER;
            // Счётчик отдельным узлом, а не в тексте кнопки: он оформляется
            // подложкой и не должен слипаться с подписью.
            if (typeof o.count === 'number') btn.appendChild(el('span', 'fq__opt-n', String(o.count)));
            btn.addEventListener('click', () => {
                picked[steps[index].key] = o;
                step = index + 1;
                draw(true);
            });
            return btn;
        }

        function drawDone() {
            done.textContent = '';
            done.appendChild(el('span', 'fq__done-t', 'Подобрали:'));
            // ⛔ СВОЕГО ЧИСЛА НАЙДЕННОГО ЗДЕСЬ НЕТ. Оно уже есть у фильтра
            // («Найдено N планировок · M квартир»), и вторая копия того же
            // смысла разошлась бы с первой. Пусть говорит один источник.
            steps.forEach(s => {
                if (picked[s.key]) done.appendChild(el('span', 'fq__chip', picked[s.key].label));
            });
            const again = el('button', 'fq__again', 'заново');
            again.type = 'button';
            again.dataset.ym = YM_QUIZ_AGAIN;
            again.addEventListener('click', reset);
            done.appendChild(again);
        }

        /**
         * @param {boolean} [byAnswer] перерисовка ПОСЛЕ ответа человека (а не
         *        из-за нового набора данных). ⛔ Ответы применяются только на
         *        этом пути: apply() из общей перерисовки означал бы, что
         *        опросник дёргает фильтр в ответ на ответ фильтра — круг.
         */
        function draw(byAnswer) {
            // Данных ещё нет (первый набор не приехал) — спрашивать не о чем.
            if (quizBase.length === 0) return;
            dots.textContent = '';
            steps.forEach((_, i) => {
                dots.appendChild(el('i', i < step ? 'is-done' : i === step ? 'is-now' : ''));
            });

            if (step >= steps.length) {
                // Ни одного вопроса не задали — показывать «Подобрали:» пусто и
                // тем более применять нечего.
                if (!steps.some(st => picked[st.key])) {
                    mute = true;
                    box.classList.add('jk-hidden');
                    return;
                }
                back.classList.add('jk-hidden');
                ask.classList.add('jk-hidden');
                opts.classList.add('jk-hidden');
                done.classList.remove('jk-hidden');
                drawDone();
                if (byAnswer && !applied) {
                    applied = true;
                    apply();
                }
                return;
            }

            const list = steps[step].opts();
            // Вариантов нет (одна комнатность / нет скидок / меньше трёх цен) —
            // шаг пропускаем целиком, а не показываем пустую строку.
            if (list.length === 0) {
                step++;
                draw(byAnswer);
                return;
            }

            done.classList.add('jk-hidden');
            ask.classList.remove('jk-hidden');
            opts.classList.remove('jk-hidden');
            back.classList.toggle('jk-hidden', step === 0);
            ask.textContent = steps[step].ask;
            opts.textContent = '';
            list.forEach(o => opts.appendChild(optionBtn(o, step)));
        }

        /**
         * Применить ответы. Комнатность, срок, этаж, бюджет и отделка — через
         * договор фильтра (настоящий отбор по всему блоку); скидка — сужением
         * плиток сводки, потому что такого поля у фильтра нет.
         */
        function apply() {
            quizPick = {
                rooms: picked.rooms ? picked.rooms.rooms : null,
                budget: picked.budget ? picked.budget.budget || null : null,
                deadline: picked.deadline ? picked.deadline.deadline || null : null,
                floor: picked.floor ? picked.floor.floor || null : null,
                renovation: picked.renovation ? picked.renovation.renovation || null : null,
                discount: !!(picked.discount && picked.discount.discount),
            };
            quizDone = true;
            quizScrolled = false;
            askFilter(quizPick);
        }

        function reset() {
            steps.forEach(s => delete picked[s.key]);
            step = 0;
            applied = false;
            mute = false;
            quizPick = { rooms: null, budget: null, deadline: null, floor: null, renovation: null, discount: false };
            quizDone = false;
            quizScrolled = false;
            draw();
            // «Заново» обязано вернуть и ФИЛЬТР — ВСЕ применённые им поля
            // (комнатность, бюджет, срок, этаж, отделка).
            // ⛔ Снять одну комнатность и оставить цену со сроком значило бы
            // показать человеку чистый опрос над сужённой выдачей.
            askFilter(quizPick);
        }

        back.addEventListener('click', () => {
            if (step === 0) return;
            step--;
            // Ответ, к которому вернулись, снимаем: иначе он молча остался бы
            // в «Подобрали», хотя человек его переспрашивает.
            delete picked[steps[step].key];
            draw();
        });

        return {
            node: box,
            /** Варианты пересчитываются на каждый новый набор, шаг — нет. */
            refresh: draw,
            /**
             * Начать опрос заново, НЕ трогая фильтр: его уже сбросили снаружи
             * (кнопка «Сбросить»). Тем же кодом, что и «заново», но без
             * askFilter — иначе сброс порождал бы второй запрос следом за первым.
             */
            forget: () => {
                steps.forEach(st => delete picked[st.key]);
                step = 0;
                applied = false;
                mute = false;
                draw();
            },
            setVisible: on => box.classList.toggle('jk-hidden', mute || !on),
        };
    }

    // ═══ Каркас выдачи (создаётся один раз) ═══
    let dom = null;

    /**
     * SSR-контент выдачи (блок 1.7.4): таблица типов планировок, отрисованная
     * сервером ВНУТРИ #jkFilterResults. Её задача — быть содержимым секции до
     * (и без) JS: бот и человек с выключенными скриптами видят планировки, цены
     * «от» и остаток в продаже.
     *
     * Мы её ПЕРЕИСПОЛЬЗУЕМ, а не затираем на старте: каркас выдачи строится
     * рядом (грид, «показать ещё», подвал добавляются ПОСЛЕ таблицы), поэтому
     * секция не мигает пустотой, пока летит первый запрос. Таблица уходит ровно
     * в тот момент, когда её заменили настоящие карточки, — и остаётся на месте,
     * если фильтр вообще не поднялся (см. teardown).
     */
    let ssrNode = root.querySelector('[data-ssr="flats"]');

    function dropSsr() {
        if (!ssrNode) return;
        if (ssrNode.parentNode) ssrNode.parentNode.removeChild(ssrNode);
        ssrNode = null;
    }

    function buildViewToggle() {
        const toggle = el('div', 'jk-filter__view');
        toggle.setAttribute('role', 'group');
        toggle.setAttribute('aria-label', 'Вид выдачи');
        const btns = VIEWS.map(mode => {
            const btn = el('button', 'jk-filter__view-btn');
            btn.type = 'button';
            btn.dataset.view = mode;
            btn.dataset.ym = 'jk_filter_view_toggle';
            btn.appendChild(icon(VIEW_ICONS[mode]));
            btn.appendChild(el('span', 'jk-filter__view-full', VIEW_LABELS[mode]));
            if (VIEW_LABELS_SHORT[mode]) {
                btn.appendChild(el('span', 'jk-filter__view-short', VIEW_LABELS_SHORT[mode]));
            }
            // Полная подпись остаётся у кнопки всегда — на узком экране у «Сводки»
            // видна короткая, и без aria-label скринридер прочитал бы «Свод».
            btn.setAttribute('aria-label', VIEW_LABELS[mode]);
            btn.addEventListener('click', () => setView(mode));
            toggle.appendChild(btn);
            return btn;
        });
        return { toggle: toggle, btns: btns };
    }

    function buildDom() {
        if (dom) return dom;

        // Страховка кросс-версий (1.7.4): `.jk-filter--ssr` гасит управление панели
        // до прихода фасетов, и снимает его свежий jk-filter.js. Но в браузере может
        // лежать закэшированная версия фильтра, которая про этот класс не знает и
        // снимает только `.jk-hidden` — тогда шапка, диапазоны и полоса сортировки
        // (а вместе с ней и переключатель видов) остались бы невидимыми навсегда.
        // Раз мы дошли до отрисовки — данные есть, панель обязана быть рабочей.
        const panel = root.closest('.jk-filter');
        if (panel) panel.classList.remove('jk-filter--ssr');

        // ⛔ ОПРОСНИК ЖИВЁТ В САМОЙ ПАНЕЛИ, А НЕ НАД НЕЙ (просьба владельца:
        // «чтоб место не занимал»). Первым узлом панели — там же, где он стоял
        // в принятом черновике. Панель не нашлась (разметка поехала) — опросник
        // просто не появляется: он надстройка, а не условие работы выдачи.
        if (panel) {
            quizApi = buildQuiz();
            panel.insertBefore(quizApi.node, panel.firstChild);
        }

        const built = buildViewToggle();
        // Переключатель вида селится в ОДНУ строку с сортировкой: своей полосой
        // над выдачей он съедал ~60px пустоты на широком экране. .jk-filter__bar —
        // часть блока фильтра; если разметка поедет, откатываемся на свою шапку.
        const bar = document.querySelector('.jk-filter__bar');
        let viewHost;
        if (bar) {
            bar.appendChild(built.toggle);
            viewHost = built.toggle;
        } else {
            viewHost = el('div', 'jk-filter__results-head');
            viewHost.appendChild(built.toggle);
            root.appendChild(viewHost);
        }

        const grid = el('div', 'jk-filter__grid');
        grid.addEventListener('click', onCardClick);

        const moreWrap = el('div', 'jk-filter__more jk-hidden');
        const moreBtn = el('button', 'jk-filter__more-btn', 'Показать ещё');
        moreBtn.type = 'button';
        moreBtn.dataset.ym = 'jk_filter_load_more';
        moreBtn.addEventListener('click', () => {
            // Планировки сервер отдаёт ОДНИМ куском (пагинации у view=plans нет),
            // поэтому здесь «показать ещё» — чистая дорисовка уже полученного:
            // ни запроса, ни места в ведре 120/60.
            // ⛔ Сверяем ещё и renderedMode: человек мог переключить вид, пока
            // ответ квартир летел — тогда `view` уже 'plans', а в `items` лежат
            // КВАРТИРЫ, и planKey() на них бросает исключение на каждой карточке
            // (кнопка мертвела бы молча). Ответ нового режима придёт и перерисует
            // выдачу сам.
            // Сводка показывает ВСЕ строки сразу — дозагружать ей нечего.
            // ⛔ Отдельной ветки «сводка» в syncChrome НЕТ и не нужно: ответ
            // планировок ставит hasMore = false при любых `pages` (см.
            // onResults), а morePlans считается только для вида «Планировки» —
            // то есть на своих данных кнопка и так спрятана.
            // Этот выход держит ДРУГОЙ промежуток: человек нажал «Сводку» из
            // списка, ответ планировок ещё летит, на экране остаются квартиры
            // вместе со своим живым «показать ещё» (смена вида syncChrome не
            // зовёт — см. setView). Без выхода клик по ней ушёл бы в loadMore()
            // за квартирами чужого режима.
            if (view === 'summary') return;
            if (view === 'plans') {
                if (renderedMode !== 'plans') return;
                const first = growPlans(PLANS_STEP);
                const wasFocused = document.activeElement === moreBtn;
                syncChrome();
                nudgeMore();
                // Набор кончился — кнопка уехала под jk-hidden вместе с фокусом.
                // Уронить фокус на body значит заставить клавиатурного человека
                // начать обход страницы заново: переводим на первую новую карточку.
                if (wasFocused && moreWrap.classList.contains('jk-hidden')
                    && first && typeof first.focus === 'function') {
                    first.focus();
                }
                return;
            }
            moreBtn.disabled = true;
            // loadMore() отдаёт null, когда страниц больше нет: ответа, который
            // снял бы блокировку, не будет — снимаем сами, иначе кнопка залипает.
            if (api.loadMore() === null) moreBtn.disabled = false;
        });
        moreWrap.appendChild(moreBtn);

        const foot = el('div', 'jk-filter__foot');
        const catalog = el('a', 'jk-filter__catalog');
        catalog.dataset.ym = 'jk_filter_catalog_link';
        catalog.appendChild(el('span', null, 'Открыть в полном каталоге'));
        catalog.appendChild(icon('fas fa-arrow-right'));
        foot.appendChild(catalog);

        const empty = el('div', 'jk-filter__empty jk-hidden');
        empty.appendChild(icon('fas fa-magnifying-glass'));
        empty.appendChild(el('span', 'jk-filter__empty-text',
            'По фильтру ничего не найдено — измените или сбросьте фильтры'));
        const emptyBtn = el('button', 'jk-filter__empty-btn', 'Сбросить');
        emptyBtn.type = 'button';
        emptyBtn.dataset.ym = 'jk_filter_reset_empty';
        emptyBtn.addEventListener('click', () => api.reset());
        empty.appendChild(emptyBtn);

        root.appendChild(grid);
        root.appendChild(moreWrap);
        // ⛔ Витрина квартир дома: «полный каталог» — это она сама, и ссылка вела бы на ту же
        // страницу через 301. Подвал не встаёт в разметку вовсе — не прячется, а не рисуется.
        if (!ON_SHOWCASE) root.appendChild(foot);
        root.appendChild(empty);

        dom = {
            viewHost: viewHost, grid: grid, moreWrap: moreWrap, moreBtn: moreBtn,
            foot: foot, catalog: catalog, empty: empty, viewBtns: built.btns,
        };
        syncViewButtons();
        return dom;
    }

    /**
     * Клик по выдаче. Пока ответ нового режима не пришёл, на экране ОСТАЮТСЯ
     * карточки прежнего (гаснут под is-loading, но кликабельны). На медленной
     * сети человек успевает нажать «Планировки» и попасть по квартире — тогда
     * открылась бы модалка от вида, который он уже покинул. И зеркально:
     * карточка планировки в режиме квартир слала бы событие раскрытия. Поэтому
     * карточки чужого режима клик просто не обслуживают (fix-round-5 P1).
     */
    function onCardClick(e) {
        // Сводка обслуживает свои строки сама (аккордеон висит на кнопке строки),
        // а карточек квартир/планировок в ней нет вовсе.
        if (view === 'summary') return;
        // Строка РАСКРЫТОЙ планировки живёт внутри той же сетки и открывает ту же
        // модалку, что карточка квартиры, — проверяем её первой.
        const row = e.target.closest('.jk-filter__panel-row');
        if (row) {
            openFlat(e, row);
            return;
        }
        // ⛔ ЧЕРТЁЖ — ЧУЖАЯ ЦЕЛЬ. По обложке открывается окно крупного плана
        //    (js/plan-modal.js, делегат на документе), и панель квартир при том
        //    же нажатии раскрываться НЕ должна: одно нажатие — одно действие.
        //    Выходим молча, без preventDefault: событие обязано дойти до окна.
        if (e.target.closest('.jk-filter__plan-cover')) return;
        const plan = e.target.closest('.jk-filter__plan');
        if (plan) {
            if (view === 'plans') onPlanClick(plan);
            return;
        }
        if (view === 'plans') return;
        const card = e.target.closest('.jk-filter__card, .jk-filter__row');
        if (card) openFlat(e, card);
    }

    function openFlat(e, node) {
        // Ctrl/Cmd/Shift — пользователь ЯВНО просит новую вкладку, не мешаем.
        if (e.ctrlKey || e.metaKey || e.shiftKey) return;
        const id = parseInt(node.dataset.flatId, 10) || 0;
        if (id <= 0 || typeof window.openFlatOverlay !== 'function') return;
        e.preventDefault();
        window.openFlatOverlay(id);
    }

    /**
     * Клик по карточке планировки объявляется событием — раскрытие (ниже)
     * слушает именно его, а не вызывает функцию напрямую: так аккордеон
     * остаётся отключаемым и проверяемым отдельно от рендера карточек.
     * Передаём НАБОР ФИЛЬТРА карточки, а не plan_id: именно (rooms, area_min,
     * area_max) переживает следующий запрос к API.
     */
    function onPlanClick(card) {
        card.dispatchEvent(new CustomEvent('bronidom:plan-card-click', {
            bubbles: true,
            detail: {
                rooms: parseInt(card.dataset.rooms, 10),
                areaMin: parseFloat(card.dataset.areaMin),
                areaMax: parseFloat(card.dataset.areaMax),
                count: parseInt(card.dataset.count, 10) || 0,
                label: String(card.dataset.planLabel || ''),
                card: card,
            },
        }));
    }

    // ═══ Аккордеон квартир планировки (блок 1.7.3) ═══
    /**
     * Раскрыта ВСЕГДА не больше одной планировки. Причина не в оформлении:
     * квартиры каждой планировки — отдельный запрос к flat_api, и все они делят
     * с выдачей одно ведро лимита (flat_list_jk_, 120/60). Десяток открытых
     * аккордеонов = десяток запросов подряд и 429 в середине подбора.
     */
    let openCard = null;    // карточка с раскрытой панелью
    let openDetail = null;  // её отбор {rooms, areaMin, areaMax, label…}
    let panel = null;       // сама панель
    let panelBody = null;
    let panelCount = null;
    let panelTerm = null;    // значение «Срок сдачи» в сводке (заполняется ответом)
    let panelTermKey = null; // и его подпись
    let panelStatus = null;  // живая область в шапке: «Загружаем квартиры…»
    // Номер раскрытия. Ответ прежнего клика, доехавший после закрытия/смены
    // карточки, обязан быть выброшен — сравниваем токен.
    let panelToken = 0;

    /** «2-комн. · 42.2–43.8 м²» — подпись планировки из данных карточки. */
    function planTitle(detail) {
        const range = areaRangeText({ area_min: detail.areaMin, area_max: detail.areaMax });
        const label = String(detail.label || '') || roomsLabel(detail.rooms);
        return range === '' ? label : label + ' · ' + range;
    }

    /**
     * Этаж и корпус строкой. От общего metaParts() отличается тем, что
     * «Апартаменты» здесь НЕ текст в общей куче: в панели это отдельный бейдж
     * (юр-маркировку человек обязан замечать, а не вычитывать из хвоста строки).
     */
    function panelFloorText(f) {
        const parts = [];
        const floor = parseInt(f.floor, 10);
        if (Number.isInteger(floor)) {
            const total = parseInt(f.floors_total, 10);
            parts.push(floor + (Number.isInteger(total) && total > 0 ? '/' + total : '') + ' эт.');
        }
        const section = String(f.section == null ? '' : f.section).trim();
        if (section !== '') parts.push(section);
        return parts.length > 0 ? parts.join(' · ') : '—';
    }

    /**
     * Срок сдачи КОРПУСА готовой строкой («Сдан 2025» / «Строится · 4 кв 2026»).
     * Считает его сервер (BuildingCorpusBreakdown), фронт только показывает.
     *
     * ⚠️ view=flats эти поля сегодня НЕ отдаёт — их несёт сгруппированный ответ
     * (listGrouped). Читаем защитно: появятся в ответе — строка сразу оживёт,
     * не появятся — панель просто молчит про срок, а не рисует пустой бейдж.
     */
    function panelTermText(f) {
        const text = String(f.section_text == null ? '' : f.section_text).trim();
        if (text !== '') return text;
        const state = String(f.section_state == null ? '' : f.section_state).trim();
        if (state === 'done') return 'Сдан';
        if (state === 'building') return 'Строится';
        return '';
    }

    /** Строка квартиры: этаж/корпус/срок · площадь · цена (уценка и ₽/м²). */
    function panelRow(f) {
        const row = anchor(f, 'jk-filter__panel-row');

        const floor = el('span', 'jk-filter__panel-floor');
        floor.appendChild(el('span', 'jk-filter__panel-floor-text', panelFloorText(f)));
        const term = panelTermText(f);
        if (term !== '') floor.appendChild(el('span', 'jk-filter__panel-term', term));
        // Апартаменты — нежилое помещение (нет прописки, выше налог).
        if (f.is_apartment) floor.appendChild(el('span', 'jk-filter__panel-apart', 'Апартаменты'));
        row.appendChild(floor);

        row.appendChild(el('span', 'jk-filter__panel-area', fmtArea(f.area) + ' м²'));

        const price = el('span', 'jk-filter__panel-price');
        const top = el('span', 'jk-filter__panel-price-top');
        const pct = discountPct(f);
        // Уценка ПЕРЕД ценой, а не после: тогда сама цена всегда прижата к правому
        // краю и колонка чисел выстраивается по одной линии. При обратном порядке
        // строки со скидкой уводили цену влево, и список читался лесенкой.
        if (pct > 0) {
            top.appendChild(el('span', 'jk-filter__panel-old', fmtPrice(f.price_old)));
            top.appendChild(el('span', 'jk-filter__panel-badge', '−' + pct + '%'));
        }
        top.appendChild(el('span', 'jk-filter__panel-price-val', fmtPrice(f.price)));
        price.appendChild(top);
        // ₽/м² — вторая строка мелким: именно по ней сравнивают лоты внутри
        // одной планировки (площади там почти равны, а цена гуляет на этажах).
        const m2 = parseInt(f.price_m2, 10) || 0;
        if (m2 > 0) price.appendChild(el('span', 'jk-filter__panel-m2', nf.format(m2) + ' ₽/м²'));
        row.appendChild(price);
        return row;
    }

    /**
     * Сообщение вместо списка. Молчащая панель неотличима от «зависла», поэтому
     * ЛЮБОЙ исход (загрузка / сбой / пусто) говорит о себе текстом.
     */
    function panelNote(text, retryable) {
        const note = el('div', 'jk-filter__panel-note', text);
        // Живая область ставится на СООБЩЕНИЕ, а не на список: иначе скринридер
        // зачитал бы вслух все сто строк квартир. «Загружаем/не удалось/ничего
        // не найдено» человек обязан услышать — он же сам нажал.
        note.setAttribute('role', 'status');
        note.setAttribute('aria-live', 'polite');
        if (retryable !== true) return note;
        const btn = el('button', 'jk-filter__panel-retry', 'Повторить');
        btn.type = 'button';
        btn.dataset.ym = 'jk_filter_plan_retry';
        btn.addEventListener('click', () => reloadPanel());
        note.appendChild(btn);
        return note;
    }

    /**
     * Заглушка на время запроса: строки-призраки + резерв высоты под будущий
     * список.
     *
     * Зачем резерв. Раньше панель появлялась строчкой «Загружаем квартиры…»
     * (~50px), а через полсекунды скачком вырастала до 420px — всё, что ниже,
     * прыгало ДВАЖДЫ за одно нажатие. Карточка планировки знает, сколько у неё
     * квартир, поэтому высоту будущего списка можно занять сразу: min(count,
     * влезающие) × высота строки, но не выше потолка самого списка.
     */
    function panelSkeleton(count) {
        const box = el('div', 'jk-filter__panel-skeleton');
        const m = panelMetrics();
        const known = parseInt(count, 10) || 0;
        // Сколько строк ФИЗИЧЕСКИ влезает в потолок списка — столько и максимум
        // резервируем: выше потолка список всё равно прокручивается внутри себя.
        const fit = Math.max(1, Math.floor(m.max / m.row));
        const rows = Math.min(known > 0 ? known : PANEL_SKELETON_DEFAULT, fit);
        for (let i = 0; i < rows; i++) {
            box.appendChild(el('span', 'jk-filter__panel-skel'));
        }
        // Полос РОВНО столько, сколько строк в резерве: полосы фиксированной
        // высоты в высоком резерве оставляли бы пустой хвост — «загрузилось
        // пусто». Высота полосы приезжает переменной, сами полосы — из css.
        // Это CSSOM (element.style), а не инлайн-атрибут шаблона — CSP цела.
        box.style.minHeight = (rows * m.row) + 'px';
        box.style.setProperty('--jkf-skel-h', (m.row - 4) + 'px');
        return box;
    }

    function setPanelBody(node, busy) {
        if (!panel) return;
        panelBody.textContent = '';
        panelBody.appendChild(node);
        // ⛔ aria-busy живёт на СПИСКЕ, а не на всей панели. Пока он висел на
        // панели, живая область «Загружаем квартиры…» оказывалась ВНУТРИ
        // busy-поддерева — скринридер её не зачитывал, и человек, нажавший
        // карточку, слышал тишину. Теперь статус стоит в шапке, вне списка.
        panelBody.setAttribute('aria-busy', busy === true ? 'true' : 'false');
        // Один голос на панель: пока грузим — говорит шапка; дальше говорит то,
        // что нарисовали (ошибка / «не найдены» несут свой role=status).
        if (panelStatus) panelStatus.textContent = busy === true ? 'Загружаем квартиры…' : '';
    }

    /** Панель уже стёрта из DOM вместе с сеткой — просто забываем состояние. */
    function forgetPanel() {
        openCard = null;
        openDetail = null;
        panel = null;
        panelBody = null;
        panelCount = null;
        panelTerm = null;
        panelTermKey = null;
        panelStatus = null;
        // Токен сдвигаем: летящий ответ прежнего раскрытия рисовать больше некуда.
        panelToken++;
        // Показывать этот ответ уже негде, а место в ведре 120/60 он занимает —
        // обрываем, а не ждём молча.
        if (typeof api.cancelPlanFlats === 'function') api.cancelPlanFlats();
    }

    /**
     * Узел, доигрывающий сворачивание. Держим ровно один: если человек щёлкает
     * планировками быстрее анимации, прежний «призрак» убираем сразу — иначе они
     * копятся в сетке и разносят раскладку.
     */
    let closingPanel = null;

    /**
     * Снять панель с экрана. `animate` — дать ей свернуться (высота 1fr→0fr в
     * css), иначе убрать мгновенно.
     *
     * id и role снимаются СРАЗУ, ещё до анимации: пока призрак доигрывает, в
     * документе не должно быть второго узла с тем же id — иначе aria-controls
     * новой панели указывал бы на уходящую.
     */
    function detachPanel(node, animate) {
        if (!node || !node.parentNode) return;
        node.removeAttribute('id');
        node.removeAttribute('role');
        node.setAttribute('aria-hidden', 'true');
        // Внутри узла — фокусируемые ссылки-квартиры. `aria-hidden` без `inert`
        // означал бы 180мс, когда Tab уводит фокус в скрытое от скринридера
        // поддерево (нарушение ARIA). inert выключает и фокус, и клики.
        node.inert = true;
        if (!animate) {
            node.parentNode.removeChild(node);
            return;
        }
        if (closingPanel && closingPanel !== node && closingPanel.parentNode) {
            closingPanel.parentNode.removeChild(closingPanel);
        }
        closingPanel = node;
        node.classList.add('jk-filter__panel--closing');
        node.classList.remove('is-open');
        window.setTimeout(() => {
            if (node.parentNode) node.parentNode.removeChild(node);
            if (closingPanel === node) closingPanel = null;
        }, PANEL_CLOSE_MS);
    }

    /**
     * @param {boolean} [animate] Плавно свернуть (человек сам закрыл панель).
     *   При переключении на ДРУГУЮ планировку — false: две анимации навстречу
     *   друг другу выглядят дёрганьем, да и место старой панели нужно новой.
     */
    function closePanel(animate) {
        if (!openCard) return;
        const card = openCard;
        if (panel && panel.parentNode) {
            // Фокус мог уехать внутрь панели (Tab по квартирам). Убрать её из-под
            // фокуса, не вернув его на карточку, значит уронить фокус на body —
            // клавиатурный человек начал бы обход страницы заново.
            if (panel.contains(document.activeElement) && typeof card.focus === 'function') {
                card.focus();
            }
            detachPanel(panel, animate === true && !motionOff());
        }
        card.setAttribute('aria-expanded', 'false');
        card.removeAttribute('aria-controls');
        // Клик, который свернул аккордеон, — своя цель метрики: делегат
        // [data-ym] читает её уже ПОСЛЕ нашего обработчика.
        card.dataset.ym = YM_COLLAPSE;
        forgetPanel();
    }

    /**
     * Левая половина раскрытия: КРУПНЫЙ чертёж планировки + её сводка.
     *
     * Данные берутся с самой карточки (dataset) — те же, что уже приехали в
     * ответе view=plans. Ни одного нового запроса раскрытие не делает: меняется
     * только ширина у картинки-прокси (400 → 800).
     */
    function panelAside(detail) {
        const box = el('div', 'jk-filter__panel-plan');
        const card = detail.card;
        const src = String((card && card.dataset.planSrc) || '');
        const cover = el('div', src === ''
            ? 'jk-filter__panel-cover jk-filter__panel-cover--empty'
            : 'jk-filter__panel-cover');
        if (src === '') {
            cover.appendChild(icon('fas fa-drafting-compass'));
        } else {
            const img = document.createElement('img');
            img.src = thumbUrl(src, PLAN_LARGE_WIDTH);
            // eager, а не lazy: панель вставляется РОВНО во вьюпорт по нажатию
            // человека — откладывать её главную картинку значит показывать пустой
            // прямоугольник там, куда он только что ткнул. У карточек сетки lazy
            // остаётся: их сотня и большинство за экраном.
            img.setAttribute('loading', 'eager');
            img.setAttribute('decoding', 'async');
            img.alt = 'Планировка ' + planTitle(detail);
            // I.7: битый чертёж → та же иконка-заглушка, что при пустом адресе
            // (вместе с обёрткой-лупой, если она есть).
            coverFallback(cover, img, 'jk-filter__panel-cover--empty', 'fas fa-drafting-compass');
            // Лупа на чертёж: 560px в панели мало, чтобы разглядеть комнаты и
            // размеры. Клик открывает ОБЩИЙ лайтбокс (window.openLightbox из
            // utils.js) — ровно тот же, что у поэтажного плана в оверлее
            // квартиры, без второй реализации зума.
            //
            // Адрес прогоняем через safeSrc (паритет с PHP LayoutPolicy): битая
            // запись фида не должна дать `javascript:` или чужой хост в href.
            // Не прошёл — картинка остаётся без лупы, но САМА панель работает.
            const full = safeSrc(src);
            if (full === '') {
                cover.appendChild(img);
            } else {
                // <a href> — прогрессивный фолбэк: если лайтбокса на странице нет
                // (старый utils.js / JS отвалился), чертёж просто откроется
                // вкладкой, а не «клик в никуда». Приём взят у поэтажного плана
                // в flat-overlay.js (data-floorplan).
                const zoom = document.createElement('a');
                zoom.className = 'jk-filter__panel-zoom';
                // ⛔ На проде — исходник (лупа и должна открывать полный чертёж
                //    застройщика), на стенде — своя миниатюра: иначе нажатие
                //    без JS уводит человека С КОПИИ САЙТА на CDN застройщика.
                zoom.href = standSrc(full, 1200);
                zoom.target = '_blank';
                zoom.rel = 'noopener noreferrer';
                zoom.setAttribute('aria-label', 'Открыть планировку в полном размере');
                zoom.dataset.planFull = full;
                zoom.dataset.ym = 'jk_filter_plan_zoom';
                zoom.addEventListener('click', ev => {
                    if (typeof window.openLightbox !== 'function') return;
                    ev.preventDefault();
                    // kinds:['plan'] — белая подложка: чертежи приходят
                    // прозрачными PNG, на тёмном фоне линий не видно.
                    window.openLightbox([full], 0, { kinds: ['plan'], standSrc: (u) => standSrc(u, 1200) });
                });
                zoom.appendChild(img);
                cover.appendChild(zoom);
            }
        }
        box.appendChild(cover);

        const facts = el('dl', 'jk-filter__panel-facts');
        const fact = (label, value) => {
            if (value === '') return;
            facts.appendChild(el('dt', 'jk-filter__panel-fact-key', label));
            facts.appendChild(el('dd', 'jk-filter__panel-fact-val', value));
        };
        fact('Площадь', areaRangeText({ area_min: detail.areaMin, area_max: detail.areaMax }));
        const from = parseInt(card && card.dataset.planPrice, 10) || 0;
        fact('Цена', from > 0 ? 'от ' + fmtPrice(from) : '');
        const count = parseInt(detail.count, 10) || 0;
        fact('В продаже', count > 0 ? count + ' ' + flatsWord(count) : '');
        // Срок сдачи приезжает вместе с квартирами (b.deadline в ответе).
        //
        // Пара рисуется СРАЗУ и держит свою строку, а значение до ответа —
        // полоса-заглушка (та же шиммер-эстетика, что у скелетона списка).
        // Раньше пара была скрыта до ответа и возникала через полсекунды после
        // нажатия, толкая сводку на +19px — второй прыжок за одно нажатие,
        // ровно то самое «контент дышит» (замер на проде 31.07: сводка 166→185).
        // Если срока у дома нет вовсе — пара прячется в paintPanelTerm, и это
        // редкий случай против прыжка на КАЖДОМ раскрытии.
        panelTermKey = el('dt', 'jk-filter__panel-fact-key', 'Срок сдачи');
        panelTerm = el('dd', 'jk-filter__panel-fact-val jk-filter__panel-fact-pending');
        facts.appendChild(panelTermKey);
        facts.appendChild(panelTerm);
        box.appendChild(facts);
        return box;
    }

    function openPanel(detail) {
        const card = detail.card;
        panel = el('div', 'jk-filter__panel');
        panel.id = PANEL_ID;
        panel.setAttribute('role', 'region');
        panel.setAttribute('aria-label', 'Квартиры планировки ' + planTitle(detail));

        // Внутренняя обёртка нужна анимации: сама панель — грид с одной строкой
        // 0fr→1fr, а вся видимая часть (рамка, фон, отступы) живёт внутри и
        // подрезается overflow. Без неё рамка схлопнутой панели осталась бы
        // видна полоской.
        const inner = el('div', 'jk-filter__panel-inner');
        const head = el('div', 'jk-filter__panel-head');
        head.appendChild(el('span', 'jk-filter__panel-title', planTitle(detail)));
        // Живая область — В ШАПКЕ, вне списка с aria-busy: иначе «Загружаем…»
        // не доходит до скринридера (см. setPanelBody).
        panelStatus = el('span', 'jk-filter__panel-status');
        panelStatus.setAttribute('role', 'status');
        panelStatus.setAttribute('aria-live', 'polite');
        head.appendChild(panelStatus);
        panelCount = el('span', 'jk-filter__panel-total');
        head.appendChild(panelCount);
        // Видимая стрелка «свернуть»: карточка-переключатель при раскрытии
        // уезжает из виду (панель встаёт под ВЕСЬ ряд сетки), и способ закрыть
        // существовал, не будучи виден. ⛔ Второго канала состояния нет — кнопка
        // зовёт ТОТ ЖЕ onPlanExpand с открытой планировкой, то есть идёт путём
        // «повторный клик по своей карточке». Фокус оттуда же: после клика он
        // внутри панели, и closePanel вернёт его на карточку. data-ym — та же
        // цель, что у сворачивания карточкой (делегат читает нажатый узел).
        const collapse = el('button', 'jk-filter__panel-collapse');
        collapse.type = 'button';
        collapse.setAttribute('aria-label', 'Свернуть планировку');
        collapse.dataset.ym = YM_COLLAPSE;
        collapse.appendChild(icon('fas fa-chevron-up'));
        collapse.addEventListener('click', () => {
            if (openDetail) onPlanExpand(openDetail);
        });
        head.appendChild(collapse);
        inner.appendChild(head);

        const main = el('div', 'jk-filter__panel-main');
        main.appendChild(panelAside(detail));
        panelBody = el('div', 'jk-filter__panel-body');
        main.appendChild(panelBody);
        inner.appendChild(main);
        // ⛔ Вторая кнопка ВНИЗУ панели (слово владельца 27.09.2026, телефон: у планировки
        // много квартир — внизу длинного списка стрелки «свернуть» в шапке не видно, и
        // приходилось листать наверх). Тот же путь, что у стрелки в шапке (повторный клик
        // по своей карточке), плюс возврат экрана к карточке планировки.
        const foot = el('div', 'jk-filter__panel-foot');
        const collapseFoot = el('button', 'jk-filter__panel-collapse-foot');
        collapseFoot.type = 'button';
        collapseFoot.dataset.ym = YM_COLLAPSE;
        collapseFoot.appendChild(icon('fas fa-chevron-up'));
        collapseFoot.appendChild(el('span', '', 'Свернуть, к планировкам'));
        collapseFoot.addEventListener('click', () => {
            const card = openCard;
            if (openDetail) onPlanExpand(openDetail);
            if (card && typeof card.scrollIntoView === 'function') {
                card.scrollIntoView({ block: 'center', behavior: 'smooth' });
            }
        });
        foot.appendChild(collapseFoot);
        inner.appendChild(foot);
        panel.appendChild(inner);
        // Панель — соседний узел карточки в той же сетке (она растянута на всю
        // ширину, см. css). В DOM она сразу за своей карточкой, поэтому Tab с
        // карточки уходит в её квартиры. ⚠️ При двух и более колонках визуальный
        // порядок с этим НЕ совпадает: панель встаёт под ВЕСЬ ряд, и карточки,
        // стоящие в разметке после неё, видны ВЫШЕ панели (см. dense в css) —
        // осознанный размен на отсутствие дырок в сетке.
        card.parentNode.insertBefore(panel, card.nextSibling);
        // Раскрытие «с нуля»: класс вешаем ПОСЛЕ того, как браузер увидел узел в
        // схлопнутом состоянии (getBoundingClientRect форсирует пересчёт).
        // Иначе вставка и класс попадают в один кадр — перехода не будет вовсе.
        if (!motionOff()) panel.getBoundingClientRect();
        panel.classList.add('is-open');
        // Карточка нижнего ряда: панель целиком уезжает за нижний край экрана и
        // тап выглядит как «ничего не произошло». Подтягиваем её в кадр — тот же
        // приём, что у «Показать ещё» (nudgeMore). Уже ПОСЛЕ старта раскрытия,
        // иначе скролл считает панель нулевой высоты.
        if (typeof panel.scrollIntoView === 'function') {
            panel.scrollIntoView({ block: 'nearest' });
        }

        openCard = card;
        openDetail = detail;
        card.setAttribute('aria-expanded', 'true');
        card.setAttribute('aria-controls', PANEL_ID);
        card.dataset.ym = YM_EXPAND;
        reloadPanel();
    }

    /** Запрос квартир текущей раскрытой планировки (он же повтор после сбоя). */
    function reloadPanel() {
        if (!openDetail || !panel) return;
        const token = ++panelToken;
        // Не голая строчка «Загружаем…», а скелетон с резервом высоты под
        // будущий список: иначе контент под панелью прыгает дважды за клик.
        setPanelBody(panelSkeleton(openDetail.count), true);
        api.fetchPlanFlats({
            rooms: openDetail.rooms,
            areaMin: openDetail.areaMin,
            areaMax: openDetail.areaMax,
        })
            .then(res => {
                if (token !== panelToken || !panel) return;
                if (res === null) {
                    // Фильтр сменился, пока ответ летел: показывать квартиры
                    // прежнего набора нельзя, а молчать — тем более.
                    // Полосу-заглушку срока снимаем: квартир не будет, значит и
                    // срок взять неоткуда — иначе она мерцала бы вечно.
                    paintPanelTerm(null);
                    setPanelBody(panelNote('Фильтр изменился — откройте планировку заново.', false), false);
                    return;
                }
                paintPanelFlats(res.flats, res.total, res.catalogUrl);
            })
            .catch(err => {
                if (token !== panelToken || !panel) return;
                console.warn('jk-filter раскрытие планировки:', err && err.message);
                // Та же причина, что и выше: без квартир срока не будет.
                paintPanelTerm(null);
                setPanelBody(panelNote(panelErrorText(err), err && err.retryable === true), false);
            });
    }

    function panelErrorText(err) {
        const reason = err && err.reason;
        if (reason === 'rate_limit') return 'Слишком много запросов — подождите пару секунд.';
        if (reason === 'timeout') return 'Сервер долго не отвечает.';
        return 'Не удалось загрузить квартиры этой планировки.';
    }

    /**
     * Хвост «показаны первые N из M» + ссылка в полный каталог.
     *
     * Страница раскрытия — 100 квартир (серверный потолок), а на проде у ЖК 39
     * есть кластер студий на 337 available. Без этой строки карточка обещает
     * «337 квартир», панель показывает 100, и 237 недостижимы вообще ничем.
     */
    function panelOverflow(shown, total, catalogUrl) {
        const box = el('div', 'jk-filter__panel-more');
        box.appendChild(el('span', 'jk-filter__panel-more-text',
            'Показаны первые ' + shown + ' из ' + total + ' ' + flatsWord(total)));
        const href = typeof catalogUrl === 'string' && catalogUrl !== '' ? catalogUrl : '';
        if (href === '') return box;
        const link = el('a', 'jk-filter__panel-more-link',
            'Показать все ' + total + ' в каталоге');
        link.href = href;
        link.dataset.ym = 'jk_filter_plan_catalog';
        box.appendChild(link);
        return box;
    }

    /**
     * Срок ЖК в сводке планировки: квартиры одной планировки бывают в разных
     * корпусах. Общий диапазон приходит со страницей, deadline — срок лота.
     */
    function paintPanelTerm(first) {
        if (!panelTerm || !panelTermKey) return;
        const pageDeadline = window.JK && window.JK.deadline_display !== undefined
            ? window.JK.deadline_display : window.BRONI_JK_DEADLINE_DISPLAY;
        const heroDeadline = document.getElementById('statDeadline');
        const text = String(pageDeadline !== undefined ? pageDeadline : (heroDeadline ? heroDeadline.textContent : '')).trim();
        const value = text === 'срок уточняется' ? '' : text;
        // Заглушка отработала — дальше живёт обычное значение.
        panelTerm.classList.remove('jk-filter__panel-fact-pending');
        panelTerm.textContent = value;
        panelTermKey.classList.toggle('jk-hidden', value === '');
        panelTerm.classList.toggle('jk-hidden', value === '');
    }

    /**
     * «Посмотреть на генплане» в раскрытой панели (слово владельца 25.09.2026).
     *
     * Корпуса планировки — из секций её квартир, которые панель уже получила; какие
     * из них обведены на схеме, отвечает компактный генплан страницы (js/jk-genplan.js)
     * на синхронный вопрос `broni:genplan-find` (страница комнатности и — с 26.09.2026 —
     * карточка ЖК). Генплана с обводкой нет (вопрос никто не слушает) или корпус не
     * обведён — кнопки нет.
     * Нажатие — `broni:genplan-locate`: страница поднимается к генплану, корпуса
     * подсвечены, у генплана кнопка «Вернуться к планировке» (панель остаётся открытой).
     */
    function panelGenplan(rows) {
        const aside = panel ? panel.querySelector('.jk-filter__panel-plan') : null;
        if (!aside || aside.querySelector('.jk-filter__panel-genplan')) return;
        const sections = [];
        rows.forEach(f => {
            const section = String(f.section == null ? '' : f.section).trim();
            const number = String(f.section_number == null ? '' : f.section_number).trim();
            if (section !== '' && !sections.some(s => s.section === section && s.number === number)) {
                sections.push({ section, number });
            }
        });
        if (sections.length === 0) return;
        const ask = { sections, ids: [] };
        document.dispatchEvent(new CustomEvent('broni:genplan-find', { detail: ask }));
        if (!Array.isArray(ask.ids) || ask.ids.length === 0) return;
        const btn = el('button', 'jk-filter__panel-genplan jk-genplan-show');
        btn.type = 'button';
        btn.dataset.hint = 'Покажем на генплане дом, где эта планировка';
        btn.dataset.ym = 'jk_filter_plan_genplan';
        btn.setAttribute('aria-label', 'Посмотреть на генплане. Покажем дом, где эта планировка');
        btn.appendChild(icon('fa-solid fa-map-location-dot'));
        btn.appendChild(el('span', '', 'Посмотреть на генплане'));
        btn.addEventListener('click', () => {
            document.dispatchEvent(new CustomEvent('broni:genplan-locate', {
                detail: { ids: ask.ids, what: 'планировка', back: 'Вернуться к планировке', from: btn },
            }));
        });
        aside.appendChild(btn);
    }

    function paintPanelFlats(list, total, catalogUrl) {
        // Те же правила, что у выдачи: битая запись (id ≤ 0) не рисуется, дубли
        // одной квартиры схлопываются.
        const rows = usable(list, new Set(), flatKey);
        paintPanelTerm(rows[0]);
        if (rows.length === 0) {
            setPanelBody(panelNote('Квартиры этой планировки не найдены — возможно, их уже забронировали.', false), false);
            panelCount.textContent = '';
            return;
        }
        // Счётчик в шапке — ВСЕГДА полное число: оно же стоит на карточке
        // планировки, и расхождение читалось бы как ошибка сайта.
        const all = Number.isInteger(total) && total > rows.length ? total : rows.length;
        const box = el('div', 'jk-filter__panel-list');
        const listBox = el('div', 'jk-filter__panel-rows');
        rows.forEach(f => {
            try {
                listBox.appendChild(panelRow(f));
            } catch (e) {
                console.warn('jk-filter раскрытие: строка пропущена —', e && e.message);
            }
        });
        box.appendChild(listBox);
        if (all > rows.length) box.appendChild(panelOverflow(rows.length, all, catalogUrl));
        setPanelBody(box, false);
        panelGenplan(rows);
        // Узел уже в документе — только теперь высоту строки можно замерить.
        rememberRowHeight(listBox);
        // Подтягиваем в кадр СПИСОК, а не верх панели: на телефоне шапка,
        // чертёж и сводка занимают почти экран, и «ради чего тапнул» оказывалось
        // бы за сгибом. Раскрытие к этому моменту уже доиграло.
        if (typeof panelBody.scrollIntoView === 'function') {
            panelBody.scrollIntoView({ block: 'nearest' });
        }
        // Число — золотым, слово — обычным: это главная цифра раскрытия.
        // textContent счётчика остаётся тем же «N квартир».
        panelCount.textContent = '';
        panelCount.appendChild(el('span', 'jk-filter__panel-total-num', String(all)));
        panelCount.appendChild(document.createTextNode(' ' + flatsWord(all)));
    }

    /**
     * Карточки на экране всё ещё описывают ТЕКУЩИЙ фильтр? Между движением
     * слайдера и новой выдачей есть окно (debounce 320мс + полёт запроса), в
     * котором на экране лежат карточки ПРЕЖНЕГО набора. Раскрытие такой карточки
     * ушло бы с новыми фильтрами и старым диапазоном площади — в лучшем случае
     * пусто («уже забронировали»), в худшем — чужие квартиры.
     */
    function cardsAreStale() {
        if (root.classList.contains('is-loading')) return true;
        if (typeof api.currentSig !== 'function' || renderedSig === null) return false;
        return api.currentSig() !== renderedSig;
    }

    /**
     * Раскрытие/сворачивание. Повторный клик по той же карточке сворачивает —
     * иначе единственным способом закрыть панель был бы клик по другой.
     */
    function onPlanExpand(detail) {
        if (!detail || !detail.card) return;
        const same = openCard === detail.card;
        // Свернуть можно всегда: закрытие ничего не запрашивает. Плавно — только
        // когда человек СВОРАЧИВАЕТ (клик по той же карточке); при переходе на
        // другую планировку старая уходит мгновенно, освобождая место новой.
        closePanel(same);
        if (same) return;
        if (cardsAreStale()) {
            // Раскрытия НЕ было — значит и цели «раскрыл планировку» быть не
            // должно: делегат [data-ym] читает атрибут уже ПОСЛЕ нас и иначе
            // засчитал бы клик, который ничего не сделал. Снимаем на время
            // всплытия и возвращаем следующим тиком — карточка живая, следующий
            // клик (когда выдача догрузится) обязан считаться.
            const card = detail.card;
            const goal = card.dataset.ym;
            delete card.dataset.ym;
            window.setTimeout(() => {
                if (card.dataset.ym === undefined) card.dataset.ym = goal;
            }, 0);
            return;
        }
        openPanel(detail);
    }

    /**
     * Открыто ли поверх выдачи окно — боковое окно квартиры или окно крупного
     * плана. Опознаём по классам обоих боевых окон, а видимость меряем коробкой:
     * скрытый узел даёт нулевой размер, а `offsetParent` у `position: fixed`
     * всегда null и на этот вопрос не отвечает.
     */
    function isOverlayOpen() {
        const list = document.querySelectorAll('.flat-overlay, .zpo-win');
        for (let i = 0; i < list.length; i++) {
            const n = list[i];
            if (n.hasAttribute('hidden')) continue;
            const cs = window.getComputedStyle(n);
            if (cs.display === 'none' || cs.visibility === 'hidden') continue;
            const r = n.getBoundingClientRect();
            if (r.width > 0 && r.height > 0) return true;
        }
        return false;
    }

    if (EXPAND_SUPPORTED) {
        root.addEventListener('bronidom:plan-card-click', e => onPlanExpand(e.detail));
        // Esc закрывает раскрытую планировку и возвращает фокус на её карточку —
        // мышью панель закрывается кликом по карточке, клавиатуре нужен выход.
        root.addEventListener('keydown', e => {
            if (e.key !== 'Escape' || !openCard) return;
            // ⛔ ESC ЗАКРЫВАЕТ ТОЛЬКО ВЕРХНЕЕ ОКНО, А НЕ ВСЁ СРАЗУ. Поймано
            //    28.08.2026: человек открывал квартиру из панели, жал Esc — и
            //    вместе с окном схлопывалась вся панель под ним, планировку
            //    приходилось раскрывать заново. Виноваты были ДВА обработчика на
            //    одну клавишу: боковое окно закрывало себя, а этот закрывал
            //    панель, хотя её никто не просил.
            //    Оба окна (`.flat-overlay`, `.zpo-win`) живут прямыми детьми
            //    <body>, а этот обработчик — на контейнере выдачи, то есть
            //    ГЛУБЖЕ. Пока фокус остаётся на карточке внутри выдачи, событие
            //    приходит сюда ПЕРВЫМ, до обработчиков окон на документе, —
            //    значит окно ещё в разметке и его видно. Перехват (capture) для
            //    этого не нужен: он понадобился бы обработчику на window.
            // ⛔ ВИДИМОСТЬ МЕРИМ РАЗМЕРОМ, А НЕ `offsetParent`. У элементов с
            //    `position: fixed` (а оба окна именно такие) offsetParent РАВЕН
            //    null ВСЕГДА, даже когда окно раскрыто на весь экран. Размер
            //    врать не умеет: скрытый узел даёт нулевую коробку.
            if (isOverlayOpen()) return;
            const card = openCard;
            closePanel(true);
            if (typeof card.focus === 'function') card.focus();
        });
    }

    // ═══ Вид «плитка / список / планировки» ═══
    // Новичок видит дефолт (1.7.4 — «Планировки»); вернувшийся человек видит
    // СВОЙ выбор: localStorage пишется только по клику на переключателе, поэтому
    // смена дефолта чужие предпочтения не перетирает.
    let view = DEFAULT_VIEW;
    // ⛔ СТРАНИЦА КОМНАТНОСТИ ОТКРЫВАЕТСЯ «ПЛАНИРОВКАМИ» ВСЕГДА (слово владельца
    // 25.09.2026: блок квартир там главный и открывается видом со сгруппированными
    // планировками, окном и панелью квартир). Память браузера — общая с карточкой ЖК,
    // и выбранный там «Список» здесь её не перебивает. Признак страницы — приказ
    // сервера о комнатности (window.BRONI_JK_ROOMS_PINNED, тот же, что читает фильтр).
    const roomsPage = Boolean(window.BRONI_JK_ROOMS_PINNED) && typeof window.BRONI_JK_ROOMS_PINNED === 'object'
        && VIEWS.indexOf('plans') >= 0;
    // Вкладка «Планировки» витрины дома (27.09.2026) — тоже всегда «Планировки»: это и есть
    // её содержание, а свои «Плитка / Список / Сводка» у витрины над сеткой. Память браузера
    // карточки («Список») здесь не действует и не пишется: кнопок вида блока на витрине нет.
    if (roomsPage || (ON_SHOWCASE && VIEWS.indexOf('plans') >= 0)) {
        view = 'plans';
    } else {
        try {
            const saved = window.localStorage.getItem(VIEW_KEY);
            if (VIEWS.indexOf(saved) >= 0) view = saved;
        } catch (e) {
            // Приватный режим/запрет хранилища — остаёмся на дефолтном виде.
        }
    }
    // Адрес страницы важнее памяти браузера, и правит он в ОБЕ стороны: ссылка
    // ?fv=plans открывает планировки у того, кто смотрел список, а ?fv=flats —
    // квартиры у того, кто смотрел планировки. Иначе присланная ссылка меняла бы
    // смысл на чужом экране (fix-round-5 P1).
    const urlMode = MODES_SUPPORTED ? api.urlMode : null;
    if (urlMode === 'plans') view = 'plans';
    // ⛔ Сверяем РЕЖИМ, а не имя вида: `?fv=flats` у человека с сохранённой
    // «Сводкой» иначе оставлял бы её на экране, а следом мы бы сами дёрнули
    // setResultMode('plans') — то есть адрес страницы молча проигрывал бы памяти
    // браузера ровно в том случае, ради которого его и читают.
    else if (urlMode === 'flats' && modeOf(view) === 'plans') view = 'grid';
    // Режим фильтра выставляем ДО подписки: иначе первый же запрос ушёл бы за
    // квартирами, а через миг мы бы дёрнули второй — за планировками. Адрес при
    // этом НЕ трогаем: вид восстановлен из памяти браузера, а не выбран сейчас.
    if (MODES_SUPPORTED && api.resultMode !== modeOf(view)) {
        api.setResultMode(modeOf(view), { writeUrl: false });
    }

    function syncViewButtons() {
        if (!dom) return;
        dom.grid.dataset.view = view;
        dom.viewBtns.forEach(btn => {
            btn.setAttribute('aria-pressed', btn.dataset.view === view ? 'true' : 'false');
        });
    }

    function setView(next, remember) {
        if (VIEWS.indexOf(next) < 0 || next === view) return;
        const modeChanged = modeOf(next) !== modeOf(view);
        view = next;
        // Переключение НЕ руками (строка комнатности с генплана) выбор человека не пишет:
        // память браузера — только за кликом по переключателю.
        if (remember !== false) {
            try {
                window.localStorage.setItem(VIEW_KEY, next);
            } catch (e) {
                // Не смогли запомнить — вид всё равно переключаем, просто без памяти.
            }
        }
        syncViewButtons();
        if (!modeChanged) {
            // Плитка ↔ список: перерисовываем ИЗ УЖЕ ПОЛУЧЕННЫХ данных — смена
            // вида это оформление, а не новый фильтр, дёргать сеть (и терять
            // «показать ещё») незачем.
            repaint();
            return;
        }
        // Квартиры ↔ планировки: набор данных ДРУГОЙ. Фильтр отдаст его из
        // своего кэша (если этот режим уже смотрели при том же фильтре) или
        // сходит в сеть. Уже показанное не стираем — пусть погаснет под
        // is-loading, как при смене любого фильтра, иначе выдача моргает пустотой.
        api.setResultMode(modeOf(next));
    }

    // ═══ «Сразу к планировкам» (слово владельца 28.09.2026) ═══
    // «Любая страница комнатности начиналась с блока планы той квартиры, куда я перешёл, а
    // не на генплан опять»; «при клике на комнатность открываются сразу раздел планировки и
    // сами квартиры». Ссылка с якорем `#planirovki` (полоса комнатности — components/
    // jk-rooms-nav.php, строки комнатности генплана — js/jk-genplan.js) открывает страницу
    // на полосе «Найдено · сортировка · вид» над карточками планировок — не на шапке
    // фильтра и не на генплане сверху. Прямой заход без якоря (из поиска) — сверху, как был:
    // H1 и заголовок видит каждый, кто пришёл из выдачи.
    // ⛔ Якорь снимается сразу после перехода: «Назад» с квартиры и обновление страницы
    // не должны снова дёргать человека к планировкам (браузер вернёт его прокрутку сам).
    const LANDING_RE = /^#planirovki(?:&|$)/;
    let landPending = LANDING_RE.test(String((window.location && window.location.hash) || ''));
    function landingNode() {
        const panel = root.closest ? root.closest('.jk-filter') : null;
        const bar = panel ? panel.querySelector('.jk-filter__bar') : null;
        return bar && bar.getClientRects().length ? bar : root;
    }
    function landingTop(node) {
        const sticky = document.querySelector('.jk-sticky-nav.visible:not(.scroll-hidden)');
        return Math.max(0, Math.round(node.getBoundingClientRect().top + window.scrollY - 12
            - (sticky ? sticky.offsetHeight : 0)));
    }
    function landOnPlans() {
        if (!landPending) return;
        landPending = false;
        try {
            window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
        } catch (e) {
            // Нет History API — якорь остаётся в адресе, переход всё равно сделан.
        }
        if (typeof window.scrollTo !== 'function') return;
        const go = () => {
            const top = landingTop(landingNode());
            window.scrollTo({ top, behavior: 'instant' });
            return top;
        };
        const later = typeof window.requestAnimationFrame === 'function'
            ? window.requestAnimationFrame.bind(window)
            : (fn => setTimeout(fn, 0));
        later(() => {
            const first = go();
            // Выше блока ещё может дорасти вёрстка (фасеты фильтра, шрифты). Поправляем
            // один раз и только если человек сам не двигал страницу.
            setTimeout(() => {
                if (Math.abs(window.scrollY - first) > 2) return;
                if (Math.abs(landingTop(landingNode()) - first) > 24) go();
            }, 600);
        });
    }

    // ЛЮБОЕ нажатие на генплане — корпус на схеме, кнопка корпуса, строка корпуса в списке
    // или подсказке, строка «Студии · от … ₽» — ведёт к ПЛАНИРОВКАМ этого корпуса (слово
    // владельца 28.09.2026: «любое нажатие на генплане… к списку планировок»). Вид
    // переключается на «Планировки» без записи в память браузера: это не выбор вида
    // человеком, а переход с генплана. Слушаем только генплан, который сужает ЭТОТ блок
    // (карточка ЖК и страница группы — `data-genplan-results="#jkFlats"`); на вкладке
    // витрины вид и так «Планировки», ею управляет js/flats.js.
    const genplanTargetsUs = () => {
        const plan = document.querySelector('[data-genplan-compact]');
        const own = root.closest ? root.closest('#jkFlats') : null;
        return Boolean(plan && own && plan.getAttribute('data-genplan-results') === '#jkFlats');
    };
    document.addEventListener('broni:genplan-corpus', event => {
        const detail = event && event.detail;
        if (!detail || !detail.section || detail.matched === 0 || !genplanTargetsUs()) return;
        if (VIEWS.indexOf('plans') >= 0 && view !== 'plans') setView('plans', false);
    });

    // ═══ Отрисовка ═══
    let items = [];          // показанные квартиры (страница 1 + все «показать ещё»)
    let seen = new Set();    // их id — защита от дубля страницы при ретрае
    let renderedSig = null;  // подпись фильтра, которому принадлежит список на экране
    let renderedMode = 'flats'; // режим, которому принадлежат карточки на экране
    let renderedCatalogUrl = '';
    let hasMore = false;
    // Сколько карточек планировок УЖЕ нарисовано (вид «Планировки» режется на
    // клиенте: PLANS_FIRST сразу, дальше по PLANS_STEP за клик).
    let planShown = 0;
    let dead = false;
    // Список стёрли из-за сбоя запроса — это НЕ «по фильтру ничего не найдено».
    let cleared = false;

    const makeCard = f => {
        if (view === 'plans') return planCard(f);
        return view === 'list' ? listRow(f) : gridCard(f);
    };

    /**
     * Сборка узлов пачкой. try/catch на КАЖДУЮ карточку: одна битая запись фида
     * не должна ронять всю выдачу (без него список [квартира, null] давал молча
     * пустой грид — исключение убивало цикл на первом же элементе).
     */
    function appendNodes(list) {
        const frag = document.createDocumentFragment();
        list.forEach(f => {
            try {
                frag.appendChild(makeCard(f));
            } catch (e) {
                console.warn('jk-filter выдача: карточка пропущена —', e && e.message);
            }
        });
        dom.grid.appendChild(frag);
    }

    /**
     * Дорисовать ещё `count` карточек планировок из уже полученных данных.
     *
     * Порция — ЧИСЛО КАРТОЧЕК (PLANS_FIRST / PLANS_STEP), а не ряды: ширину
     * сетки больше не спрашиваем у браузера, поэтому на телефоне и на 1920px
     * человек видит одно и то же число планировок.
     *
     * @returns {?Element} Первая дорисованная карточка (для возврата фокуса).
     */
    function growPlans(count) {
        const step = Math.max(1, parseInt(count, 10) || 0);
        const next = items.slice(planShown, planShown + step);
        const at = dom.grid.querySelectorAll('.jk-filter__plan').length;
        planShown += next.length;
        appendNodes(next);
        const drawn = dom.grid.querySelectorAll('.jk-filter__plan');
        return drawn.length > at ? drawn[at] : null;
    }

    /**
     * Номер перерисовки. Нужен ОДНОМУ месту — опроснику: он обязан различать
     * «фильтр уже перерисовал экран сам» и «никто ничего не сделал, рисуй сам».
     * Без счётчика опросник либо рисовал дважды, либо не рисовал вовсе.
     */
    let repaintSeq = 0;

    /** Полная перерисовка (замена набора или смена вида). */
    function repaint() {
        repaintSeq++;
        const d = buildDom();
        // Раскрытая планировка живёт ВНУТРИ сетки — чистка стирает и её. Состояние
        // аккордеона надо забыть здесь, иначе он останется «открытым» на карточке,
        // которой уже нет в документе.
        forgetPanel();
        d.grid.textContent = '';
        // Новый набор / другая сортировка / смена вида — показ планировок
        // начинается заново с первых девяти (PLANS_FIRST).
        planShown = 0;
        // Сводка строится из ТЕХ ЖЕ items (это floorplans) — отдельного набора и
        // отдельного запроса у неё нет, поэтому она не может разойтись с фильтром.
        // ⛔ try/catch, как у appendNodes на каждой карточке. Сводка строится
        // ОДНИМ узлом, поэтому исключение внутри неё роняло бы весь блок разом —
        // а dropSsr() к этому моменту уже снял серверную таблицу типов, то есть
        // человек остался бы с пустой секцией и без единого признака, что что-то
        // пошло не так. Сообщение честнее молчания.
        // Снимок дома для опросника берётся из ТОГО ЖЕ набора, что рисует
        // сводку и планировки, — своего источника у него нет.
        if (modeOf(view) === 'plans' && renderedMode === 'plans') quizRemember(items);
        if (view === 'summary') {
            try {
                d.grid.appendChild(summaryPane(items));
                // Опрос завершён — раскрываем подходящую строку на КАЖДОЙ
                // перерисовке: ответ фильтра приходит своим набором, и без
                // этого выбранная комнатность снова оказывалась свёрнутой.
                if (quizDone) quizOpenRow();
            } catch (e) {
                console.warn('jk-filter сводка: не собралась —', e && e.message);
                d.grid.appendChild(summaryNote(
                    'Не удалось показать сводку — откройте «Планировки».'));
            }
        } else if (view === 'plans') growPlans(PLANS_FIRST);
        else appendNodes(items);
        syncChrome();
        syncQuiz();
    }

    /**
     * Опросник виден только в «Сводке» — там, где он показывает найденное.
     * ⛔ В «Плитке» и «Списке» его нет намеренно: варианты ответов считаются по
     * планировкам, а в этих видах приезжают КВАРТИРЫ. Опрос, посчитанный по
     * одному набору и применённый к другому, разошёлся бы с экраном.
     */
    function syncQuiz() {
        if (!quizApi) return;
        // ⛔ ПОСЛЕ ОТВЕТОВ ПОЛОСА ОСТАЁТСЯ, ДАЖЕ ЕСЛИ НИЧЕГО НЕ НАШЛОСЬ. Замер на
        // проде 18.08: «студия + самый дешёвый бюджет + первый срок + этаж от
        // 5-го + со скидкой» у ЖК «Догма Парк» даёт пустую выдачу — законный
        // ответ фильтра. Но пока полосу прятали по `items.length`, вместе с
        // пустотой исчезала и кнопка «заново»: человек оставался в тупике,
        // из которого выход только через чужую «Сбросить».
        quizApi.setVisible(!dead && view === 'summary' && (items.length > 0 || quizDone));
        // ⛔ try/catch, как у самой сводки: варианты ответов считаются по тем же
        // записям фида, и одна битая (геттер, бросающий исключение) убивала бы
        // ВЕСЬ блок квартир — причём уже после dropSsr(), то есть человек
        // оставался бы с пустой секцией.
        try {
            quizApi.refresh();
        } catch (e) {
            console.warn('jk-filter опросник: не собрался —', e && e.message);
            quizApi.setVisible(false);
        }
    }

    function syncChrome() {
        const d = dom;
        const empty = items.length === 0;
        // Переключатель вида после сбоя ОСТАЁТСЯ: иначе человек заперт в
        // сломавшемся режиме и не может вернуться в тот, что работал.
        d.viewHost.classList.toggle('jk-hidden', empty && !cleared);
        d.grid.classList.toggle('jk-hidden', empty);
        d.foot.classList.toggle('jk-hidden', empty);
        // «Ничего не найдено» — только про фильтр. При сбое сети текст ошибки и
        // кнопку «Повторить» показывает сам фильтр (#jkFilterError).
        d.empty.classList.toggle('jk-hidden', !empty || cleared);
        // У квартир «ещё» = следующая страница из сети, у планировок — остаток
        // уже полученного набора. Кнопка одна и та же, цель метрики разная.
        const morePlans = view === 'plans' && planShown < items.length;
        d.moreWrap.classList.toggle('jk-hidden', empty || !(hasMore || morePlans));
        d.moreBtn.textContent = morePlans
            ? 'Показать ещё (осталось ' + (items.length - planShown) + ')'
            : 'Показать ещё';
        d.moreBtn.dataset.ym = morePlans ? 'jk_filter_plans_more' : 'jk_filter_load_more';
        if (!empty) d.catalog.href = renderedCatalogUrl || api.catalogUrl();
    }

    /**
     * Записи, годные к показу: с настоящим ключом и без повторов.
     * Ключ квартиры — id (id<=0 = битая запись, ссылка flat.php?id=0 вела бы в
     * никуда), ключ планировки — комнатность+диапазон площадей.
     */
    function usable(list, known, keyFn) {
        const out = [];
        (Array.isArray(list) ? list : []).forEach(item => {
            let key = '';
            try {
                key = keyFn(item);
            } catch (e) {
                console.warn('jk-filter выдача: запись пропущена —', e && e.message);
                return;
            }
            if (key === '' || known.has(key)) return;
            known.add(key);
            out.push(item);
        });
        return out;
    }

    const flatKey = f => {
        const id = flatId(f);
        return id > 0 ? String(id) : '';
    };

    /** После дописанной страницы кнопка уезжает вниз — подтягиваем её в кадр. */
    function nudgeMore() {
        if (!dom || dom.moreWrap.classList.contains('jk-hidden')) return;
        if (typeof dom.moreBtn.scrollIntoView === 'function') {
            dom.moreBtn.scrollIntoView({ block: 'nearest' });
        }
    }

    function onResults(p) {
        if (dead || !p) return;
        // Ответ ЧУЖОГО режима (человек уже переключил вид, а старый запрос
        // только долетел). Ни дописывать, ни заменять им экран нельзя: в
        // планировках он нарисовал бы квартиры, и наоборот.
        const payloadMode = p.mode === 'plans' ? 'plans' : 'flats';
        if (MODES_SUPPORTED && payloadMode !== modeOf(view)) return;
        const plans = payloadMode === 'plans';
        const sig = String(p.sig == null ? '' : p.sig);
        const d = buildDom();
        const page = parseInt(p.page, 10) || 1;
        const pages = parseInt(p.pages, 10) || 0;
        const keyFn = plans ? planKey : flatKey;

        if (p.append === true) {
            // Ответ принадлежит другому набору фильтров, чем список на экране —
            // это отставший «показать ещё». Дописывать его нельзя, а заменять
            // им экран тоже нельзя: актуальный ответ придёт отдельно.
            if (sig !== renderedSig || items.length === 0) return;
            const fresh = usable(p.flats, seen, keyFn);
            items = items.concat(fresh);
            hasMore = page < pages;
            // Дописываем ТОЛЬКО новые узлы: перерисовка всего списка на каждой
            // странице сбрасывала бы уже загруженные картинки планировок.
            appendNodes(fresh);
            syncChrome();
            nudgeMore();
        } else {
            // Набор данных приехал — дальше содержимым контейнера распоряжается
            // выдача, и серверная таблица типов свою работу отработала. Снимаем
            // её ДО перерисовки: иначе «ничего не найдено» соседствовало бы со
            // списком типов, который фильтр уже не описывает.
            dropSsr();
            seen = new Set();
            items = usable(plans ? p.floorplans : p.flats, seen, keyFn);
            renderedSig = sig;
            renderedMode = payloadMode;
            // Комнатность этого набора — из слепка запроса, а не из живых
            // контролов (см. renderedRooms). Старый фильтр слепка не отдаёт —
            // тогда снимок дома берётся один раз, первым набором.
            renderedRooms = p.state && typeof p.state === 'object' ? p.state.rooms : undefined;
            // ⛔ ФИЛЬТР СБРОСИЛИ МИМО ОПРОСА — ЗАБЫВАЕМ ОТВЕТЫ. Кнопка «Сбросить»
            // (шапка панели и пустое состояние) зовёт api.reset(), и опросник об
            // этом никак не узнаёт: он бы так и показывал «Подобрали: Студия, до
            // 4,3 млн» над выдачей всего дома. Два состояния с одним смыслом,
            // разошедшиеся на глазах у человека. Смотрим НА СЛЕПОК ЗАПРОСА, а не
            // на живые контролы: слепок описывает именно тот набор, что на экране.
            if (quizDone && filterIsWideOpen(p.state)) quizForget();
            cleared = false;
            renderedCatalogUrl = typeof p.catalogUrl === 'string' && p.catalogUrl !== ''
                ? p.catalogUrl
                : api.catalogUrl();
            // У планировок серверной пагинации нет — весь набор пришёл одним
            // ответом. Их «показать ещё» считается отдельно (planShown), сети
            // не касается и в hasMore не попадает.
            hasMore = !plans && page < pages;
            repaint();
            landOnPlans();
        }
        d.moreBtn.disabled = false;
    }

    function onLoading(isLoading) {
        // Ставим и ДО первой отрисовки: скринридер обязан услышать «идёт
        // загрузка» уже на первом запросе, а не начиная со второго.
        const on = isLoading === true;
        root.classList.toggle('is-loading', on);
        root.setAttribute('aria-busy', on ? 'true' : 'false');
        if (dom && !on) dom.moreBtn.disabled = false;
    }

    /** Терминальный отказ фасетов: выдачи не будет — убираем всё, что успели. */
    function teardown() {
        dead = true;
        // Переключатель вида живёт в полосе фильтра, ВНЕ контейнера выдачи —
        // чистка root его бы не убрала.
        if (dom && dom.viewHost && dom.viewHost.parentNode) {
            dom.viewHost.parentNode.removeChild(dom.viewHost);
        }
        // Опросник тоже живёт ВНЕ контейнера выдачи (внутри панели фильтра) —
        // чистка root его бы не убрала, и он остался бы спрашивать про дом,
        // у которого нет ни одной квартиры.
        if (quizApi && quizApi.node.parentNode) quizApi.node.parentNode.removeChild(quizApi.node);
        quizApi = null;
        quizBase = [];
        summaryRows = [];
        root.textContent = '';
        // SSR-таблица переживает отказ (1.7.4): фасеты могли не подняться из-за
        // сети, и стирать единственный контент секции было бы хуже, чем ничего
        // не делать. Заодно бот, попавший на страницу в такой момент, видит
        // планировки, а не пустой div.
        if (ssrNode) root.appendChild(ssrNode);
        root.classList.remove('is-loading');
        root.removeAttribute('aria-busy');
        dom = null;
        items = [];
        seen = new Set();
        planShown = 0;
        forgetPanel();
    }

    // ═══ Подписки ═══
    // onFacets ПЕРВОЙ: терминальный отказ (скрытый ЖК / нет квартир в продаже)
    // приходит подписчику сразу же, и тогда выдачу заводить не нужно вовсе.
    api.onFacets(facets => {
        if (facets === null) teardown();
    });

    if (!dead) {
        // Первый подписчик onResults включает автозагрузку фильтра — apply()
        // руками не зовём (иначе был бы лишний дубль-запрос).
        api.onResults(onResults);
        api.onLoading(onLoading);
        api.onError(err => {
            // Текст ошибки и кнопку «Повторить» показывает сам фильтр
            // (#jkFilterError). Наше дело — не залипнуть в «загрузке» и не
            // выдать «ничего не найдено» там, где на самом деле сбой.
            if (dom) dom.moreBtn.disabled = false;
            if (!dom || !err || err.stage !== 'list') return;
            // Выдача НОВОГО режима не доехала, а на экране висят карточки
            // прежнего: кликать по ним уже нельзя (см. onCardClick), значит и
            // показывать нечестно — убираем до успешного ответа.
            if (renderedMode === modeOf(view) || items.length === 0) return;
            items = [];
            seen = new Set();
            renderedSig = null;
            hasMore = false;
            cleared = true;
            repaint();
        });
    }
})();
